/* ============================================================
   GEBERT & WESCHKA — STEP-Viewer für Kunden (Fassung 1, 20.09.2026)
   Läuft komplett im Browser: occt-import-js (OpenCascade-WASM, von AXIS
   geladen) liest die STEP-Datei, three.js (self-hosted Modul) rendert.
   Die Datei verlässt den Rechner nicht — erst „Preis sofort" reicht sie per
   DOM-Ereignis „gw:handoff" ans Sofortangebot-Formular auf derselben Seite.

   Werkzeuge: Drehen · Messen (Fang: Kreismittelpunkt > Ecke > Kantenmitte >
   Kante; Kante↔Kante misst senkrecht) · Fläche (Plan/Zylinder aus dem Netz
   erkannt: Bohrungs-Ø, Abstände, Winkel, Achsabstand) · Schnitt mit gefüllter
   Schnittfläche (Stencil) · Hüllmaße · Volumen/Gewicht · Bohrungsliste.

   Geometrie-Erkennung ohne Server: je B-Rep-Fläche (occt liefert brep_faces)
   werden die Dreiecksnormalen geprüft — alle parallel = Planfläche; alle
   senkrecht zu einer gemeinsamen Achse = Zylinder (Radius aus dem Netz, auf
   0,05 mm gerundet; Bohrung, wenn die Normalen zur Achse zeigen).
   ============================================================ */
(function () {
  'use strict';
  if (window.GWStepViewer) return;

  // ── three.js laden (Muster aus gw-part-viewer.js) ──
  var threeP = null;
  function loadThree() {
    if (window.THREE && window.THREE.WebGLRenderer) return Promise.resolve(window.THREE);
    if (!threeP) threeP = new Promise(function (res, rej) {
      var url = window.GW_THREE_URL;   // nur die Kopie aus dem Theme, kein fremder Server
      if (!url) return rej(new Error('3D-Grafik nicht verfügbar'));
      var s = document.createElement('script'); s.type = 'module';
      s.textContent = 'import * as T from "' + url + '"; window.THREE = T; window.dispatchEvent(new Event("gw-three-ready"));';
      s.onerror = function () { threeP = null; rej(new Error('three')); };
      window.addEventListener('gw-three-ready', function () { res(window.THREE); }, { once: true });
      document.head.appendChild(s);
      setTimeout(function () { if (window.THREE) res(window.THREE); else { threeP = null; rej(new Error('three timeout')); } }, 20000);
    });
    return threeP;
  }
  // ── OpenCascade-WASM laden (von AXIS, CORS offen) ──
  var occtP = null;
  function loadOcct() {
    if (!occtP) occtP = new Promise(function (res, rej) {
      var base = (window.GW_OCCT_BASE || 'https://quote.gebertweschka.de').replace(/\/$/, '');
      var V = '?v=0.0.23';   // Version der Bibliothek: der Server liefert sie „immutable" (1 Jahr) → bei Update hier hochzählen
      // Fehlschlag (Verbindung weg, WASM-Download abgebrochen): Promise verwerfen, damit der nächste Versuch neu lädt
      var fail = function (e) { occtP = null; rej(new Error('CAD-Bibliothek nicht ladbar' + (e && e.message ? ' (' + e.message + ')' : ''))); };
      var init = function () { window.occtimportjs({ locateFile: function (f) { return base + '/' + f + V; } }).then(res, fail); };
      if (typeof window.occtimportjs === 'function') { init(); return; }
      var s = document.createElement('script'); s.src = base + '/occt-import-js.js' + V;
      s.onload = function () { if (typeof window.occtimportjs !== 'function') return fail(new Error('occt fehlt')); init(); };
      s.onerror = function () { s.remove(); fail(); };
      document.head.appendChild(s);
    });
    return occtP;
  }

  var fmt = function (n, d) { return Number(n).toLocaleString('de-DE', { minimumFractionDigits: d == null ? 2 : d, maximumFractionDigits: d == null ? 2 : d }); };
  /** WebGL da? Ohne (Remote-Desktop, GPU per Richtlinie aus) gar nicht erst 3 MB CAD-Bibliothek laden. */
  function hasWebGL() {
    try { var c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); }
    catch (e) { return false; }
  }
  /** Technische Fehlermeldungen in Kundensprache. */
  function kundenFehler(e) {
    var m = String(e && e.message ? e.message : e || '');
    if (/webgl/i.test(m)) return '3D-Grafik (WebGL) ist auf diesem Gerät nicht verfügbar. Preis und Bestellung funktionieren trotzdem im Sofortangebot.';
    if (/three|3D-Grafik/i.test(m)) return 'Die 3D-Anzeige konnte nicht geladen werden. Bitte die Seite neu laden.';
    if (/CAD-Bibliothek|occt/i.test(m)) return 'Die CAD-Bibliothek konnte nicht geladen werden. Bitte Verbindung prüfen und neu laden.';
    if (/memory|speicher|allocation/i.test(m)) return 'Das Modell ist für dieses Gerät zu groß. Auf einem Rechner öffnen oder als Anfrage senden.';
    return 'Die Datei konnte nicht angezeigt werden (' + m + ').';
  }

  // ── STEP → Netz + Flächenanalyse ──
  function parseStep(THREE, occt, buffer) {
    // Feine Tessellierung: Standard ist zu grob für Bohrungen unter Ø 5 und Fasen — die
    // Flächenerkennung (Zylinder/Kegel aus Normalen) braucht saubere Netze.
    var res = occt.ReadStepFile(new Uint8Array(buffer), { linearUnit: 'millimeter', linearDeflectionType: 'bounding_box_ratio', linearDeflection: 0.0005, angularDeflection: 0.1 });
    if (!res || !res.success) res = occt.ReadStepFile(new Uint8Array(buffer), null);
    if (!res || !res.success || !res.meshes || !res.meshes.length) throw new Error('STEP konnte nicht gelesen werden');
    var geoms = [], faces = [], triToFace = [], bbox = new THREE.Box3(), vol = 0, tri = 0;
    var a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
    res.meshes.forEach(function (m, mi) {
      var pos = new Float32Array(m.attributes.position.array);
      var idx = m.index && m.index.array ? Uint32Array.from(m.index.array) : null;
      var g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      if (m.attributes.normal && m.attributes.normal.array) g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(m.attributes.normal.array), 3));
      else g.computeVertexNormals();
      if (idx) g.setIndex(new THREE.BufferAttribute(idx, 1));
      g.computeBoundingBox(); bbox.union(g.boundingBox); geoms.push(g);
      var nTri = idx ? idx.length / 3 : pos.length / 9; tri += nTri;
      var map = new Int32Array(nTri).fill(-1);
      (m.brep_faces || []).forEach(function (bf) {
        var gi = faces.length; faces.push({ mesh: mi, first: bf.first, last: bf.last });
        for (var t = bf.first; t <= bf.last; t++) map[t] = gi;
      });
      triToFace.push(map);
      for (var t2 = 0; t2 < nTri; t2++) {
        var i0 = idx ? idx[3 * t2] : 3 * t2, i1 = idx ? idx[3 * t2 + 1] : 3 * t2 + 1, i2 = idx ? idx[3 * t2 + 2] : 3 * t2 + 2;
        a.fromArray(pos, 3 * i0); b.fromArray(pos, 3 * i1); c.fromArray(pos, 3 * i2);
        vol += a.dot(b.clone().cross(c)) / 6;
      }
    });
    var model = { geoms: geoms, faces: faces, triToFace: triToFace, bbox: bbox, volumeCm3: Math.abs(vol) / 1000, triCount: tri, info: [] };
    model.info = faces.map(function (_, gi) { return analyzeFace(THREE, model, gi); });
    model.holes = groupHoles(THREE, model);
    model.circles = circlesFrom(THREE, model);
    var basis = -1, bestA = 0; model.info.forEach(function (f, i) { if (f.type === 'plane' && f.area > bestA) { bestA = f.area; basis = i; } });
    model.baseFace = basis;
    return model;
  }

  /** Flächentyp aus den Dreiecksnormalen: plane / cylinder / other, mit Kennwerten. */
  function analyzeFace(THREE, model, gi) {
    var fr = model.faces[gi], g = model.geoms[fr.mesh];
    var pos = g.getAttribute('position'), idx = g.getIndex();
    var A = new THREE.Vector3(), B = new THREE.Vector3(), C = new THREE.Vector3(), n = new THREE.Vector3(), tmp = new THREE.Vector3();
    var area = 0, cen = new THREE.Vector3(), normals = [], verts = [];
    var nSum = new THREE.Vector3();
    for (var t = fr.first; t <= fr.last; t++) {
      var i0 = idx ? idx.getX(3 * t) : 3 * t, i1 = idx ? idx.getX(3 * t + 1) : 3 * t + 1, i2 = idx ? idx.getX(3 * t + 2) : 3 * t + 2;
      A.fromBufferAttribute(pos, i0); B.fromBufferAttribute(pos, i1); C.fromBufferAttribute(pos, i2);
      n.copy(B).sub(A).cross(tmp.copy(C).sub(A)); var ta = n.length() / 2; if (ta < 1e-9) continue;
      n.divideScalar(ta * 2); area += ta; nSum.addScaledVector(n, ta);
      cen.addScaledVector(A.clone().add(B).add(C).divideScalar(3), ta);
      normals.push(n.clone()); verts.push(A.clone(), B.clone(), C.clone());
    }
    if (area <= 0) return { type: 'other', area: 0, centroid: cen };
    cen.divideScalar(area);
    var out = { type: 'other', area: area, centroid: cen };
    var nMean = nSum.clone().divideScalar(area);
    var nearFlat = nMean.length() > 0.995;
    if (nearFlat) {
      // Ebenheit prüfen: flach gewölbte Flächen (großer Radius, < ~20° Bogen) haben ebenfalls eine
      // gemittelte Normale nahe 1. Echte Ebenen liegen bei ≤ 0,006 mm, die Wölbungen bei ≥ 0,29 mm.
      var nm = nMean.clone().normalize(), dev = 0, dv = new THREE.Vector3();
      verts.forEach(function (v) { var e = Math.abs(dv.copy(v).sub(cen).dot(nm)); if (e > dev) dev = e; });
      if (dev <= Math.max(0.02, 1e-4 * Math.sqrt(area))) { out.type = 'plane'; out.normal = nm; return out; }
    }
    // Rotationsfläche? Suche eine Achse, zu der alle Normalen denselben Winkel haben:
    // Zylinder → n·ax ≈ 0, Kegel (Fase/Senkung) → n·ax ≈ const ≠ 0. Kandidaten sind die
    // drei Hauptachsen der Normalenverteilung; genommen wird die mit der kleinsten Streuung.
    var cov = [0, 0, 0, 0, 0, 0];
    normals.forEach(function (v) { cov[0] += v.x * v.x; cov[1] += v.x * v.y; cov[2] += v.x * v.z; cov[3] += v.y * v.y; cov[4] += v.y * v.z; cov[5] += v.z * v.z; });
    var cands = eigenvectors3(THREE, cov); if (!cands.length) return out;
    var ax = null, axMean = 0, axStd = Infinity;
    cands.forEach(function (cand) {
      var sum = 0, sum2 = 0; normals.forEach(function (v) { var d = v.dot(cand); sum += d; sum2 += d * d; });
      var mean = sum / normals.length, std = Math.sqrt(Math.max(0, sum2 / normals.length - mean * mean));
      if (std < axStd) { axStd = std; ax = cand.clone(); axMean = mean; }
    });
    if (!ax || axStd > 0.04) {
      // keine Rotationsfläche mit fester Achse → gekrümmte Freiform (Torus/Rundung o. ä.)
      out.type = normals.length >= 4 ? 'rundung' : 'other'; return out;
    }
    if (Math.abs(axMean) > 0.08) {
      // Kegel: halber Öffnungswinkel aus dem Normalenwinkel zur Achse
      if (axMean < 0) { ax.negate(); axMean = -axMean; }
      // Fast ebene, leicht gewölbte Fläche (z. B. Zahnflanke): die mittlere Normale passt immer als
      // „Achse" → Schein-Kegel mit ~90° halbem Öffnungswinkel. Das ist keine Fase/Senkung, sondern gekrümmt.
      if (nearFlat && axMean > 0.985) { out.type = 'rundung'; return out; }
      out.type = 'cone'; out.axis = ax; out.halfAngle = THREE.MathUtils.radToDeg(Math.asin(Math.min(1, axMean)));
      var tminC = Infinity, tmaxC = -Infinity; verts.forEach(function (v) { var tt = v.dot(ax); if (tt < tminC) tminC = tt; if (tt > tmaxC) tmaxC = tt; });
      out.length = tmaxC - tminC;
      // konkav (Senkung) oder konvex (Fase): Normalen zeigen von der Achse weg → konvex
      var axisPtC = estimateAxisPoint(THREE, verts, normals, ax, cen); var inC = 0;
      if (axisPtC) { var qc = new THREE.Vector3(); normals.forEach(function (v, i) { var pp = verts[3 * i]; qc.copy(axisPtC).sub(pp); qc.addScaledVector(ax, -qc.dot(ax)); if (v.dot(qc) > 0) inC++; }); out.axisPoint = axisPtC; }
      out.hole = inC > normals.length / 2;
      return out;
    }
    // Radius: Abstand der Eckpunkte zur Achse durch den (auf die Achse projizierten) Schwerpunkt — der
    // Schwerpunkt eines Teilzylinders liegt nicht auf der Achse, deshalb Achspunkt über Normalen schätzen:
    // Punkt + r·(∓n) liegt auf der Achse; r aus dem Winkelumfang ist unbekannt → iterativ über Mittelwert.
    var axisPt = estimateAxisPoint(THREE, verts, normals, ax, cen);
    if (!axisPt) return out;
    axisPt = refineAxisPoint(THREE, verts, ax, axisPt);   // exakter Kreisfit über die Eckpunkte (µm statt ~1 µm Versatz)
    var rs = [], q = new THREE.Vector3();
    verts.forEach(function (v) { q.copy(v).sub(axisPt); q.addScaledVector(ax, -q.dot(ax)); rs.push(q.length()); });
    rs.sort(function (x, y) { return x - y; });
    var r = rs[Math.floor(rs.length / 2)];
    var spread = (rs[Math.floor(rs.length * 0.9)] - rs[Math.floor(rs.length * 0.1)]) / Math.max(r, 1e-6);
    if (spread > 0.08) return out;
    // konkav (Bohrung): Normalen zeigen zur Achse
    var inward = 0; normals.forEach(function (v, i) { var p = verts[3 * i]; q.copy(axisPt).sub(p); q.addScaledVector(ax, -q.dot(ax)); if (v.dot(q) > 0) inward++; });
    var tmin = Infinity, tmax = -Infinity; verts.forEach(function (v) { var tt = v.clone().sub(axisPt).dot(ax); if (tt < tmin) tmin = tt; if (tt > tmax) tmax = tt; });
    out.type = 'cylinder'; out.radius = Math.round(r * 20) / 20; out.axis = ax; out.axisPoint = axisPt.clone().addScaledVector(ax, tmin);
    out.length = tmax - tmin; out.hole = inward > normals.length / 2;
    // Winkelumfang: Vollzylinder (Bohrung/Zapfen) oder Teilzylinder (Rundung an einer Kante)
    var angMin = Infinity, angMax = -Infinity, ref = null;
    verts.forEach(function (v) { q.copy(v).sub(axisPt); q.addScaledVector(ax, -q.dot(ax)); if (!ref) { ref = q.clone().normalize(); return; } var ang = Math.atan2(q.clone().cross(ref).dot(ax), q.dot(ref)); if (ang < angMin) angMin = ang; if (ang > angMax) angMax = ang; });
    out.sweepDeg = ref ? THREE.MathUtils.radToDeg(angMax - angMin) : 360;
    if (!out.hole && out.sweepDeg < 200) out.type = 'fillet';   // konvexer Teilzylinder = Kantenrundung R
    return out;
  }
  function eigenvectors3(THREE, c) {
    // Alle drei Hauptachsen der symmetrischen Matrix per Deflation (Potenzmethode)
    var out = [], xx = c[0], xy = c[1], xz = c[2], yy = c[3], yz = c[4], zz = c[5];
    var M = [xx, xy, xz, xy, yy, yz, xz, yz, zz];
    for (var k = 0; k < 2; k++) {
      var v = new THREE.Vector3(0.57 + k * 0.1, 0.58 - k * 0.07, 0.59 + k * 0.03).normalize(), w = new THREE.Vector3();
      for (var i = 0; i < 80; i++) {
        w.set(M[0] * v.x + M[1] * v.y + M[2] * v.z, M[3] * v.x + M[4] * v.y + M[5] * v.z, M[6] * v.x + M[7] * v.y + M[8] * v.z);
        if (w.length() < 1e-12) break; v.copy(w.normalize());
      }
      var lam = M[0] * v.x * v.x + M[4] * v.y * v.y + M[8] * v.z * v.z + 2 * (M[1] * v.x * v.y + M[2] * v.x * v.z + M[5] * v.y * v.z);
      out.push(v.clone());
      // Deflation: M -= λ v vᵀ
      var vv = [v.x * v.x, v.x * v.y, v.x * v.z, v.y * v.x, v.y * v.y, v.y * v.z, v.z * v.x, v.z * v.y, v.z * v.z];
      for (var j = 0; j < 9; j++) M[j] -= lam * vv[j];
    }
    // Dritte Achse = Kreuzprodukt: die Potenzmethode versagt bei Eigenwert 0 (genau der
    // Zylinderachse — alle Normalen stehen senkrecht darauf) und gäbe den Startvektor zurück.
    if (out.length === 2) out.push(out[0].clone().cross(out[1]).normalize());
    return out;
  }
  function estimateAxisPoint(THREE, verts, normals, ax, cen) {
    // Für jeden Dreiecksrepräsentanten: Linie p + s·n schneidet die Achse nahe dem Achspunkt.
    // Kleinste-Quadrate: Achspunkt X minimiert Σ |(X − p) − ((X − p)·n)n|² in der Ebene ⊥ ax.
    var M = [0, 0, 0, 0, 0, 0, 0, 0, 0], rhs = new THREE.Vector3();
    var I = new THREE.Matrix3();
    for (var i = 0; i < normals.length; i++) {
      var n = normals[i], p = verts[3 * i];
      // Projektor P = I − n nᵀ
      var P = [1 - n.x * n.x, -n.x * n.y, -n.x * n.z, -n.y * n.x, 1 - n.y * n.y, -n.y * n.z, -n.z * n.x, -n.z * n.y, 1 - n.z * n.z];
      for (var k = 0; k < 9; k++) M[k] += P[k];
      rhs.x += P[0] * p.x + P[1] * p.y + P[2] * p.z; rhs.y += P[3] * p.x + P[4] * p.y + P[5] * p.z; rhs.z += P[6] * p.x + P[7] * p.y + P[8] * p.z;
    }
    // Regularisierung entlang der Achse (dort ist das System singulär)
    var lam = normals.length * 1e-3;
    M[0] += lam * ax.x * ax.x; M[1] += lam * ax.x * ax.y; M[2] += lam * ax.x * ax.z; M[3] += lam * ax.y * ax.x; M[4] += lam * ax.y * ax.y; M[5] += lam * ax.y * ax.z; M[6] += lam * ax.z * ax.x; M[7] += lam * ax.z * ax.y; M[8] += lam * ax.z * ax.z;
    rhs.addScaledVector(ax, lam * cen.dot(ax));
    I.set(M[0], M[1], M[2], M[3], M[4], M[5], M[6], M[7], M[8]);
    var det = I.determinant(); if (Math.abs(det) < 1e-12) return null;
    return rhs.clone().applyMatrix3(I.invert());
  }
  /** Kreisfit (Kåsa) in der Ebene ⊥ Achse. Die Eckpunkte der Tessellierung liegen exakt auf dem
   *  Zylinder, die Facettennormalen dagegen nur ungefähr in Richtung Achse → die Normalen-Schätzung
   *  liegt ~1 µm daneben, der Fit über die Punkte ist für rauschfreie Daten exakt (auch bei Teilbögen). */
  function refineAxisPoint(THREE, verts, ax, p0) {
    var u = Math.abs(ax.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    u.addScaledVector(ax, -u.dot(ax)).normalize(); var v = ax.clone().cross(u).normalize();
    var sxx = 0, sxy = 0, syy = 0, sx = 0, sy = 0, n = 0, bx = 0, by = 0, bz = 0, d = new THREE.Vector3();
    verts.forEach(function (p) {
      d.copy(p).sub(p0); var x = d.dot(u), y = d.dot(v), w = x * x + y * y;
      sxx += x * x; sxy += x * y; syy += y * y; sx += x; sy += y; n++; bx -= x * w; by -= y * w; bz -= w;
    });
    var M = new THREE.Matrix3().set(sxx, sxy, sx, sxy, syy, sy, sx, sy, n);
    if (n < 6 || Math.abs(M.determinant()) < 1e-9) return p0;
    var sol = new THREE.Vector3(bx, by, bz).applyMatrix3(M.clone().invert());
    var cx = -sol.x / 2, cy = -sol.y / 2, rf = Math.sqrt(Math.max(0, cx * cx + cy * cy - sol.z));
    if (!isFinite(cx) || !isFinite(cy) || !(rf > 0)) return p0;
    // Große Korrekturen (Teilbögen, große Radien: Normalen-Schätzung dort 1–4 mm daneben) nur,
    // wenn die Punkte wirklich auf dem Kreis liegen — sonst ist es kein Zylinder und die Schätzung bleibt.
    if (Math.hypot(cx, cy) > 1) {
      var ss = 0; verts.forEach(function (p) { d.copy(p).sub(p0); var x = d.dot(u) - cx, y = d.dot(v) - cy, e = Math.sqrt(x * x + y * y) - rf; ss += e * e; });
      if (Math.sqrt(ss / n) > Math.max(0.01, 1e-3 * rf)) return p0;
    }
    return p0.clone().addScaledVector(u, cx).addScaledVector(v, cy);
  }
  function smallestEigenvector(THREE, c) {
    // symmetrische 3×3 [xx xy xz; xy yy yz; xz yz zz] — Potenzmethode auf (λmax·I − C)
    var xx = c[0], xy = c[1], xz = c[2], yy = c[3], yz = c[4], zz = c[5];
    var tr = xx + yy + zz; if (tr < 1e-12) return null;
    var s = tr; // Verschiebung: alle Eigenwerte ≥ 0, λmax ≤ tr
    var v = new THREE.Vector3(0.57, 0.58, 0.59), w = new THREE.Vector3();
    for (var i = 0; i < 60; i++) {
      w.set((s - xx) * v.x - xy * v.y - xz * v.z, -xy * v.x + (s - yy) * v.y - yz * v.z, -xz * v.x - yz * v.y + (s - zz) * v.z);
      if (w.length() < 1e-12) break; v.copy(w.normalize());
    }
    return v;
  }
  function groupHoles(THREE, model) {
    // Koaxiale Flächen zusammenfassen (Achse parallel in beide Richtungen, Achslinien < 0,2 mm auseinander).
    // Vorher Raster-Schlüssel mit Vorzeichen-Normierung: Halbflächen mit gegenläufiger Achse und
    // Rundungsrauschen landeten in verschiedenen Gruppen (Ø24 der Hülse als „2 × Ø24").
    var groups = [], w = new THREE.Vector3();
    model.info.forEach(function (f, gi) {
      if (!f.hole || (f.type !== 'cylinder' && f.type !== 'cone') || !f.axisPoint) return;
      var g = null;
      for (var i = 0; i < groups.length && !g; i++) {
        var h = groups[i]; if (Math.abs(h.axis.dot(f.axis)) < 0.9999) continue;
        w.copy(f.axisPoint).sub(h.point); w.addScaledVector(h.axis, -w.dot(h.axis));
        if (w.length() < 0.2) g = h;
      }
      if (!g) { g = { axis: f.axis.clone().normalize(), point: f.axisPoint.clone(), radii: [], faces: [], depth: 0, sweep: {} }; groups.push(g); }
      if (f.type === 'cylinder') { g.radii.push(f.radius); g.depth = Math.max(g.depth, f.length); g.sweep[f.radius] = (g.sweep[f.radius] || 0) + (f.sweepDeg || 360); } else { g.senkung = f.halfAngle * 2; }
      g.faces.push(gi);
    });
    // Nur Bohrungen: Radien, die zusammen (CAD teilt Bohrungen oft in zwei Halbflächen) höchstens 190°
    // umfassen, sind Nutenden, Taschen-Eckradien oder Rillen. > 190° bleibt Bohrung — auch wenn eine
    // Querbohrung sie anschneidet (~290°).
    var holes = groups.map(function (g) { var full = g.radii.filter(function (r) { return g.sweep[r] > 190; }); if (!full.length) return null; var rs = full.slice().sort(function (a, b) { return a - b; }); return { d: Math.round(rs[0] * 40) / 20, steps: rs.slice(1).filter(function (r, i, arr) { return arr.indexOf(r) === i; }).map(function (r) { return Math.round(r * 40) / 20; }), depth: g.depth, senkung: g.senkung || null, faces: g.faces }; }).filter(Boolean);
    var byD = {}; holes.forEach(function (h) { byD[h.d] = (byD[h.d] || 0) + 1; });
    return { list: holes, summary: Object.keys(byD).map(Number).sort(function (a, b) { return a - b; }).map(function (d) { return { d: d, n: byD[d] }; }) };
  }
  function circlesFrom(THREE, model) {
    var out = [], cyl = model.info.filter(function (f) { return f.type === 'cylinder' && f.axisPoint; });
    cyl.forEach(function (f) {
      // Winkelumfang über alle koaxialen Flächen mit gleichem Radius (Bohrung in zwei Halbflächen = 360°)
      var sweep = 0, w = new THREE.Vector3();
      cyl.forEach(function (g) {
        if (Math.abs(g.radius - f.radius) > 0.01 || Math.abs(g.axis.dot(f.axis)) < 0.9999) return;
        w.copy(g.axisPoint).sub(f.axisPoint); w.addScaledVector(f.axis, -w.dot(f.axis));
        if (w.length() < 0.02) sweep += g.sweepDeg || 360;
      });
      f.sweepTotal = sweep;   // für die Beschriftung: Halbfläche einer Bohrung ist trotzdem eine Bohrung
      var base = { r: f.radius, ax: f.axis, hole: !!f.hole, sweep: sweep };
      out.push(Object.assign({ c: f.axisPoint.clone() }, base));
      if (f.length > 1e-3) out.push(Object.assign({ c: f.axisPoint.clone().addScaledVector(f.axis, f.length) }, base));
    });
    return out;
  }

  /** Kanten = Dreieckskanten, an denen zwei verschiedene B-Rep-Flächen aneinanderstoßen
   *  (Positionsvergleich, weil occt je Fläche eigene Eckpunkte anlegt). So entsteht jede
   *  Konstruktionskante — auch zwischen Flächen mit nur wenigen Grad Knick. Nahtlinien
   *  innerhalb einer Fläche (Zylindernaht) fallen raus, weil beide Seiten dieselbe Fläche sind. */
  function faceBoundaryEdges(THREE, model, mi) {
    var g = model.geoms[mi], pos = g.getAttribute('position'), idx = g.getIndex(), map = model.triToFace[mi];
    var q = 1e4, key = function (i) { return Math.round(pos.getX(i) * q) + ',' + Math.round(pos.getY(i) * q) + ',' + Math.round(pos.getZ(i) * q); };
    var edges = {}, nTri = map.length;
    for (var t = 0; t < nTri; t++) {
      var f = map[t], ids = idx ? [idx.getX(3 * t), idx.getX(3 * t + 1), idx.getX(3 * t + 2)] : [3 * t, 3 * t + 1, 3 * t + 2];
      for (var e = 0; e < 3; e++) {
        var a = ids[e], b = ids[(e + 1) % 3], ka = key(a), kb = key(b);
        if (ka === kb) continue;
        var k = ka < kb ? ka + '|' + kb : kb + '|' + ka;
        var rec = edges[k] || (edges[k] = { a: a, b: b, faces: {}, n: 0 });
        rec.faces[f] = true; rec.n++;
      }
    }
    var out = [];
    Object.keys(edges).forEach(function (k) {
      var r = edges[k], nf = Object.keys(r.faces).length;
      if (nf >= 2 || r.n === 1) out.push(pos.getX(r.a), pos.getY(r.a), pos.getZ(r.a), pos.getX(r.b), pos.getY(r.b), pos.getZ(r.b));
    });
    var eg = new THREE.BufferGeometry(); eg.setAttribute('position', new THREE.Float32BufferAttribute(out, 3));
    return eg;
  }

  /** Aus den Kantenlinien echte Kanten machen: kollineare Teilstücke werden verbunden,
   *  „Ecke" ist nur ein Punkt, an dem nicht genau zwei Stücke stoßen oder die Richtung um
   *  mehr als 30° knickt. Stücke auf Kreisbögen bleiben einzeln und haben keine Ecken/Mitten. */
  function buildEdgeModel(THREE, lines) {
    var q = 1e3, K = function (v) { return Math.round(v.x * q) + ',' + Math.round(v.y * q) + ',' + Math.round(v.z * q); };
    var raw = [];
    lines.forEach(function (l) { var a = l.geometry.getAttribute('position').array; for (var i = 0; i < a.length; i += 6) raw.push([new THREE.Vector3(a[i], a[i + 1], a[i + 2]), new THREE.Vector3(a[i + 3], a[i + 4], a[i + 5])]); });
    var adj = {};
    raw.forEach(function (sg, i) { [0, 1].forEach(function (j) { var k = K(sg[j]); (adj[k] = adj[k] || []).push(i); }); });
    var dir = function (i) { return raw[i][1].clone().sub(raw[i][0]).normalize(); };
    var isCorner = function (k) { var l = adj[k]; if (!l || l.length !== 2) return true; return Math.abs(dir(l[0]).dot(dir(l[1]))) < 0.866; };
    var used = new Uint8Array(raw.length), out = [];
    for (var i = 0; i < raw.length; i++) {
      if (used[i]) continue; used[i] = 1;
      var d = dir(i);
      var grow = function (pt, cur) {
        for (;;) {
          var k = K(pt), l = adj[k]; if (!l || l.length !== 2) return pt;
          var j = l[0] === cur ? l[1] : l[0]; if (used[j] || Math.abs(dir(j).dot(d)) < 0.99999) return pt;
          used[j] = 1; var sg = raw[j]; pt = K(sg[0]) === k ? sg[1].clone() : sg[0].clone(); cur = j;
        }
      };
      var b = grow(raw[i][1].clone(), i), a = grow(raw[i][0].clone(), i);
      var ac = isCorner(K(a)), bc = isCorner(K(b));
      // curve: nicht an BEIDEN Enden eine echte Ecke = Teil eines Bogens (auch die erste/letzte Sehne
      // am Bogenende). Gerade Kanten eines Volumenkörpers enden immer an Ecken mit ≥ 3 Stücken.
      out.push({ a: a, b: b, aCorner: ac, bCorner: bc, real: ac && bc, curve: !(ac && bc) });
    }
    return out;
  }

  /** Übergabe aus dem Angebotstool eines anderen Tabs: Das öffnende Fenster hält die Datei in
   *  window.__gwHandoff { file, ts } (gleiche Herkunft). Wir kopieren sie sofort — kein Speichern im Browser. */
  function handoffFromOpener() {
    var h = null; try { h = window.opener && window.opener.__gwHandoff; } catch (e) { h = null; }
    if (!h || !h.file || Date.now() - h.ts > 15 * 60000) return Promise.resolve(null);
    var name = h.file.name || 'bauteil.step';
    return h.file.arrayBuffer().then(function (buf) { return new File([buf], name); }).catch(function () { return null; });
  }

  // ── Viewer ──
  function Viewer(root) {
    this.root = root; this.THREE = null; this.model = null;
    this.tool = 'orbit'; this.sec = { axis: null, pos: 0.5, flip: false }; this.showDims = true; this.showEdges = true; this.ghost = false;
    this.selFaces = []; this.measurePts = []; this.measAlt = false; this.active = false; this.hover = null; this.hiliteD = null; this.labels = [];
    this.stage = root.querySelector('.gw-sv-stage'); this.canvasHost = root.querySelector('.gw-sv-canvas'); this.overlay = root.querySelector('.gw-sv-overlay');
    this.bind();
  }
  var P = Viewer.prototype;

  P.bind = function () {
    var self = this, root = this.root;
    root.querySelectorAll('input[type=file]').forEach(function (inp) { inp.addEventListener('change', function () { if (inp.files && inp.files[0]) self.load(inp.files[0]); inp.value = ''; }); });
    this.stage.addEventListener('dragover', function (e) { e.preventDefault(); self.stage.classList.add('is-over'); });
    this.stage.addEventListener('dragleave', function () { self.stage.classList.remove('is-over'); });
    this.stage.addEventListener('drop', function (e) { e.preventDefault(); self.stage.classList.remove('is-over'); var f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) self.load(f); });
    root.querySelectorAll('[data-tool]').forEach(function (b) { b.addEventListener('click', function () { self.setTool(b.getAttribute('data-tool')); }); });
    root.querySelectorAll('[data-view]').forEach(function (b) { b.addEventListener('click', function () {
      self.view(b.getAttribute('data-view'));
      if (b.closest('.gw-sv-pop-views')) self.closeMenus();   // Ansicht gewählt → Menü zu; „Teil drehen" bleibt offen für mehrfaches Drehen
    }); });
    // Aufklappmenüs der Werkzeugleiste
    Viewer.count = (Viewer.count || 0) + 1;
    root.querySelectorAll('.gw-sv-menu').forEach(function (mn, k) {
      var b = mn.querySelector('.gw-sv-mbtn'), pop = mn.querySelector('.gw-sv-pop');
      if (pop) { pop.id = 'gwsv' + Viewer.count + '-pop' + k; b.setAttribute('aria-controls', pop.id); }
      b.addEventListener('click', function () {
        var open = !mn.classList.contains('is-open'); self.closeMenus();
        if (!open) return;
        mn.classList.add('is-open'); b.setAttribute('aria-expanded', 'true');
        // Menü darf nicht unten aus der Bühne laufen (Bühne schneidet ab) → Höhe begrenzen, dann scrollt es
        if (pop) { pop.style.maxHeight = ''; var sr = self.stage.getBoundingClientRect(), pr = pop.getBoundingClientRect(); pop.style.maxHeight = Math.max(120, sr.bottom - pr.top - 10) + 'px'; }
      });
    });
    document.addEventListener('pointerdown', function (e) {
      var inRoot = root.contains(e.target), had = !!root.querySelector('.gw-sv-menu.is-open');
      self.active = inRoot;                                  // Esc wirkt nur, wenn zuletzt im Viewer geklickt wurde
      if (!inRoot || !e.target.closest('.gw-sv-menu')) {
        self.closeMenus();
        // Tipp aufs Modell, nur um ein Menü zu schließen, soll keinen Messpunkt setzen
        if (had && self.renderer && e.target === self.renderer.domElement) self._eatClick = true;
      }
    }, true);
    // Umschalter im Ergebnis (senkrecht ↔ direkt) und ✕ — Delegation, weil die Tafeln neu gerendert werden
    root.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('.gw-sv-alt, [data-clear]') : null; if (!t) return;
      if (t.classList.contains('gw-sv-alt')) { self.measAlt = !self.measAlt; self.drawMarks(); self.renderMeas(); return; }
      if (t.getAttribute('data-clear') === 'meas') { self.measurePts = []; self.measAlt = false; self.drawMarks(); self.renderMeas(); }
      if (t.getAttribute('data-clear') === 'sel') { self.selFaces = []; self.drawMarks(); self.renderSel(); }
    });
    root.querySelectorAll('[data-toggle]').forEach(function (b) { b.addEventListener('click', function () {
      var k = b.getAttribute('data-toggle'); if (k === 'dims') self.showDims = !self.showDims; if (k === 'edges') self.showEdges = !self.showEdges; if (k === 'ghost') self.ghost = !self.ghost;
      b.classList.toggle('is-on'); b.setAttribute('aria-pressed', b.classList.contains('is-on') ? 'true' : 'false'); self.applyDisplay(); self.drawDims();
    }); });
    root.querySelectorAll('[data-axis]').forEach(function (b) { b.addEventListener('click', function () { var ax = b.getAttribute('data-axis'); self.setSection(self.sec.axis === ax ? null : ax); }); });
    var range = root.querySelector('.gw-sv-range'), flip = root.querySelector('.gw-sv-flipbox');
    range.addEventListener('input', function () { if (!self.sec.axis) self.setSection('z'); self.sec.pos = Number(range.value) / 1000; self.applySection(); });
    flip.addEventListener('change', function () { if (!self.sec.axis) self.setSection('z'); self.sec.flip = flip.checked; self.applySection(); });
    root.querySelector('.gw-sv-secoff').addEventListener('click', function () { self.setSection(null); });
    root.querySelector('.gw-sv-rho').addEventListener('change', function () { self.renderInfo(); });
    root.querySelector('.gw-sv-meas-clear').addEventListener('click', function () { self.measurePts = []; self.measAlt = false; self.drawMarks(); self.renderMeas(); });
    root.querySelector('.gw-sv-go').addEventListener('click', function () { self.handoff(); });
    root.querySelectorAll('.gw-sv-sample').forEach(function (sample) { sample.addEventListener('click', function () {
      var base = (window.GW_OCCT_BASE || 'https://quote.gebertweschka.de').replace(/\/$/, ''), name = sample.getAttribute('data-sample') || 'gw-beispielteil.step';
      self.status('Beispielteil wird geladen …');
      fetch(base + '/samples/' + name).then(function (r) { return r.blob(); })
        .then(function (b) { self.load(new File([b], name)); })
        .catch(function () { self.status('Beispielteil nicht ladbar.'); });
    }); });
    // Datei aus dem Formular dieser Seite: NICHT sofort einlesen. Das Einlesen (OpenCascade im Hauptthread)
    // blockiert die Seite bei großen Teilen 15–27 s — genau während der Kunde Menge und Kontakt eintippt.
    // Stattdessen merken und einen Knopf anbieten; geladen wird erst auf Klick.
    document.addEventListener('gw:portal-file', function (ev) {
      var f = ev && ev.detail && ev.detail.file; if (!f || !/\.(step|stp)$/i.test(f.name)) return;
      // dieselbe Datei kam aus dem Viewer („Preis sofort") → nicht neu laden, Messung bleibt stehen
      if (f === self.file) { self.fromPortal = true; self.updateCta(); return; }
      if (f === self._pending) { self._herkunftP = true; return; }   // lädt gerade → gilt nach Erfolg
      self.queue(f);
    });
    var qGo = root.querySelector('.gw-sv-queued-go');
    if (qGo) qGo.addEventListener('click', function () { var f = self._queued; if (f) { self.fromPortalPending = true; self.load(f); self.fromPortalPending = false; } });
    // Knopf „Im 3D-Viewer messen" in der kleinen Vorschau des Angebotstools (gleiche Seite):
    // Viewer sofort im Vollbild öffnen — der Klick ist die Nutzergeste, die das Vollbild erlaubt.
    // Mit „Schließen" ist der Kunde wieder genau an seiner Stelle im Formular.
    document.addEventListener('gw:open-viewer', function (ev) {
      var f = ev && ev.detail && ev.detail.file; if (!f || !/\.(step|stp)$/i.test(f.name)) return;
      ev.preventDefault();
      if (!root.classList.contains('is-full')) self.enterFull();   // zuerst: Vollbild braucht die Nutzergeste
      if (self.file !== f && self._pending !== f) { self.fromPortalPending = true; self.load(f); self.fromPortalPending = false; }
      else if (self.file === f) { self.fromPortal = true; self.updateCta(); }
      else self._herkunftP = true;   // dieselbe Datei lädt gerade
    });
    // Datei wurde im Formular entfernt/zurückgesetzt → „liegt schon im Formular" stimmt nicht mehr
    document.addEventListener('gw:portal-clear', function () {
      if (self._queued) self.queue(null);
      if (self.fromPortal) { self.fromPortal = false; self.updateCta(); }
    });
    // Übergabe aus einem anderen Tab (Angebotstool auf Lohnfertigung/Startseite ohne großen Viewer):
    // der neue Tab holt die Datei direkt aus dem öffnenden Fenster. Nur mit ?gwsv=1.
    if (/[?&]gwsv=1\b/.test(location.search)) {
      try { history.replaceState(null, '', location.pathname + location.search.replace(/([?&])gwsv=1&?/, '$1').replace(/[?&]$/, '') + location.hash); } catch (e) {}
      self.status('Ihr Teil aus dem Angebot wird übernommen …'); self.stage.classList.add('is-loading');
      handoffFromOpener().then(function (f) {
        if (f) { self._fromTabPending = true; self.load(f); self._fromTabPending = false; }
        else { self.stage.classList.remove('is-loading'); self.status('Die Datei kam nicht an (Fenster geschlossen oder zu lange her) — bitte hier erneut auswählen.'); }
      });
    }
    // Vollbild: der GANZE Viewer (Bühne + Seitenleiste), damit Winkel, Messpunkte und Ergebnis sichtbar bleiben.
    // iPhone-Safari kennt kein Element-Vollbild → Ersatz: Viewer per CSS über die ganze Seite legen.
    var fsBtn = root.querySelector('.gw-sv-full');
    var fsEl = function () { return document.fullscreenElement || document.webkitFullscreenElement || null; };
    // Ersatz-Vollbild (iPhone): die Zurück-Geste soll das Vollbild schließen, nicht die Seite verlassen
    // (sonst sind Datei und Formular weg) → beim Öffnen einen Verlaufseintrag anlegen, bei popstate schließen.
    var pseudo = function (on, fromPop) {
      root.classList.toggle('is-full', on); root.classList.toggle('is-pseudo', on); document.documentElement.classList.toggle('gw-sv-lock', on); self.updateFullBtn();
      if (on && !self._pushed) { try { history.pushState({ gwsvFull: 1 }, ''); self._pushed = true; } catch (e) {} }
      else if (!on && self._pushed) {
        self._pushed = false;
        if (!fromPop) { try { if (history.state && history.state.gwsvFull) history.back(); } catch (e) {} }
      }
    };
    window.addEventListener('popstate', function () { if (root.classList.contains('is-pseudo')) pseudo(false, true); });
    self.enterFull = function () {
      var req = root.requestFullscreen || root.webkitRequestFullscreen;
      if (!req) { pseudo(true); return; }
      try { var pr = req.call(root); if (pr && pr.catch) pr.catch(function () { pseudo(true); }); } catch (err) { pseudo(true); }
    };
    if (fsBtn) fsBtn.addEventListener('click', function () {
      if (fsEl()) { (document.exitFullscreen || document.webkitExitFullscreen).call(document); return; }
      if (root.classList.contains('is-pseudo')) { pseudo(false); return; }
      self.enterFull();
    });
    var onFs = function () { var on = fsEl() === root; root.classList.toggle('is-full', on); if (on) root.classList.remove('is-pseudo'); self.updateFullBtn(); };
    document.addEventListener('fullscreenchange', onFs); document.addEventListener('webkitfullscreenchange', onFs);
    /** Vollbild verlassen (echt oder Ersatz), danach cb — z. B. damit „Preis sofort" das Formular sichtbar macht. */
    self.exitFull = function (cb) {
      if (fsEl() === root) {
        var done = function () { document.removeEventListener('fullscreenchange', done); document.removeEventListener('webkitfullscreenchange', done); cb(); };
        document.addEventListener('fullscreenchange', done); document.addEventListener('webkitfullscreenchange', done);
        (document.exitFullscreen || document.webkitExitFullscreen).call(document); return;
      }
      if (root.classList.contains('is-pseudo')) pseudo(false);
      cb();
    };
    window.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (root.querySelector('.gw-sv-menu.is-open')) { self.closeMenus(); return; }
      if (root.classList.contains('is-pseudo')) { pseudo(false); return; }
      if (!self.active && !root.classList.contains('is-full')) return;   // Esc im Formular darunter löscht keine Messung
      self.selFaces = []; self.measurePts = []; self.measAlt = false; self.hiliteD = null; self.drawMarks(); self.renderSel(); self.renderMeas(); self.renderHoles();
    });
  };

  /** Kam die Datei aus dem Formular dieser Seite (fromPortal) oder aus dem Formular eines anderen Tabs (fromTab),
   *  liegt sie dort schon: Knopf führt zurück statt erneut hochzuladen und ein zweites Formular zu starten. */
  P.updateCta = function () {
    var cta = this.root.querySelector('.gw-sv-cta'); if (!cta) return;
    var go = cta.querySelector('.gw-sv-go'), sub = cta.querySelector('.gw-sv-small');
    if (!go.hasAttribute('data-t')) { go.setAttribute('data-t', go.textContent); if (sub) sub.setAttribute('data-t', sub.textContent); }
    var back = this.fromPortal || this.fromTab;
    go.textContent = back ? 'Zurück zum Angebot' : go.getAttribute('data-t');
    if (sub) sub.textContent = this.fromPortal ? 'Ihre Datei liegt schon im Formular — Preis und Bestellung dort.'
      : this.fromTab ? 'Ihr Formular ist im anderen Tab noch offen — dieser Tab schließt sich.' : sub.getAttribute('data-t');
    cta.hidden = !this.file;
  };
  /** Datei aus dem Formular merken, ohne sie einzulesen (null = vergessen). */
  P.queue = function (f) {
    this._queued = f || null;
    var box = this.root.querySelector('.gw-sv-queued'), n = box && box.querySelector('.gw-sv-queued-n');
    if (n) n.textContent = f ? f.name : '';
    if (box) box.hidden = !f;
    this.stage.classList.toggle('has-queued', !!f);
  };
  P.closeMenus = function () {
    this.root.querySelectorAll('.gw-sv-menu.is-open').forEach(function (m) {
      var b = m.querySelector('.gw-sv-mbtn'), a = document.activeElement;
      if (b && a && m.contains(a) && a !== b) b.focus();   // Fokus nicht im versteckten Menü verlieren (Tastatur)
      m.classList.remove('is-open'); if (b) b.setAttribute('aria-expanded', 'false');
    });
  };
  P.updateFullBtn = function () {
    var b = this.root.querySelector('.gw-sv-full'); if (!b) return; var on = this.root.classList.contains('is-full');
    var l = b.querySelector('.l'); if (l) l.textContent = on ? 'Schließen' : 'Vollbild';
    var pth = b.querySelector('svg path'); if (pth) pth.setAttribute('d', on ? 'M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5' : 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5');
    b.setAttribute('aria-label', on ? 'Vollbild schließen' : 'Vollbild'); b.title = on ? 'Vollbild schließen' : 'Vollbild'; b.classList.toggle('is-on', on);
  };

  /** info = true: nur die Lade-Zusammenfassung (verschwindet von selbst, auf dem Handy ausgeblendet).
   *  Fortschritt und Fehler bleiben immer sichtbar. */
  P.status = function (txt, info) { clearTimeout(this._stT); var el = this.root.querySelector('.gw-sv-status'); el.hidden = !txt; el.textContent = txt || ''; el.classList.toggle('is-info', !!info); };

  P.load = function (file) {
    var self = this;
    if (!/\.(step|stp)$/i.test(file.name)) { this.stage.classList.remove('is-loading'); this.status('Bitte eine STEP-/STP-Datei wählen.'); return; }
    // Herkunft gilt erst nach Erfolg (frühe Abbrüche/Parse-Fehler lassen den Knopf beim bisherigen Teil stimmig)
    this._herkunftP = !!this.fromPortalPending; this._herkunftT = !!this._fromTabPending;
    if (file.size > 80 * 1024 * 1024) { this.stage.classList.remove('is-loading'); this.status('Datei größer als 80 MB — zum Ansehen bitte kleiner exportieren.'); return; }
    if (!hasWebGL()) { this.stage.classList.remove('is-loading'); this.status(kundenFehler('webgl')); return; }
    this.queue(null);
    this.status('CAD-Bibliothek wird geladen …');   // this.file erst nach Erfolg: eine kaputte Datei darf nicht ans Formular gehen
    this._pending = file; this.stage.classList.add('is-loading');
    var gen = this._gen = (this._gen || 0) + 1;   // ein älterer Ladevorgang darf einen neueren nicht überschreiben
    var t0 = performance.now();
    var done = function () { if (self._pending === file) { self._pending = null; self.stage.classList.remove('is-loading'); } };
    Promise.all([loadThree(), loadOcct(), file.arrayBuffer()]).then(function (r) {
      self.THREE = r[0]; self.status(file.size > 2 * 1024 * 1024 ? 'STEP wird gelesen … große Teile brauchen bis zu einer halben Minute' : 'STEP wird gelesen …');
      return new Promise(function (res) { setTimeout(res, 20); }).then(function () { return parseStep(self.THREE, r[1], r[2]); });
    }).then(function (model) {
      done();
      if (gen !== self._gen) return;   // inzwischen wurde ein anderes Teil angefordert
      self.file = file; self.fromPortal = self._herkunftP; self.fromTab = self._herkunftT;
      self.model = model; self.ensureScene(); self.buildScene(); self.stage.classList.add('has-model');
      self.applyViewOffset(); self.fit();   // Leisten sind erst jetzt sichtbar → freie Fläche neu bestimmen
      self.root.querySelector('.gw-sv-file').hidden = false; self.root.querySelector('.gw-sv-file-n').textContent = file.name;
      self.root.querySelector('.gw-sv-info').hidden = false;
      self.updateCta();
      self.selFaces = []; self.measurePts = []; self.measAlt = false; self.hiliteD = null;
      self.renderInfo(); self.renderHoles(); self.renderSel(); self.renderMeas(); self.drawDims(); self.drawMarks(); self.applySection();
      self.status(model.faces.length + ' Flächen · ' + model.triCount.toLocaleString('de-DE') + ' Dreiecke · ' + ((performance.now() - t0) / 1000).toFixed(1) + ' s', true);
      self._stT = setTimeout(function () { self.status(''); }, 6000);   // Ladeinfo verschwindet, Fehler bleiben stehen
    }).catch(function (e) { done(); if (gen !== self._gen) return; self.status(kundenFehler(e)); self.updateCta(); });
  };

  P.ensureScene = function () {
    if (this.renderer) return;
    var T = this.THREE, host = this.canvasHost, self = this;
    var renderer = new T.WebGLRenderer({ antialias: true, stencil: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2)); renderer.setSize(host.clientWidth, host.clientHeight);
    renderer.localClippingEnabled = true; renderer.setClearColor(0x0f141b, 1); host.appendChild(renderer.domElement);
    var scene = new T.Scene(); var cam = new T.PerspectiveCamera(40, host.clientWidth / host.clientHeight, 0.1, 100000); cam.up.set(0, 0, 1);
    scene.add(new T.HemisphereLight(0xdde6f2, 0x1a2130, 0.9));
    var key = new T.DirectionalLight(0xffffff, 1.1); key.position.set(1, -1.2, 1.6); scene.add(key);
    var fill = new T.DirectionalLight(0x9db4d8, 0.45); fill.position.set(-1.5, 1, 0.6); scene.add(fill);
    this.renderer = renderer; this.scene = scene; this.cam = cam;
    this.groups = { model: new T.Group(), helper: new T.Group(), cap: new T.Group(), mark: new T.Group() };
    scene.add(this.groups.model, this.groups.helper, this.groups.cap, this.groups.mark);
    this.plane = new T.Plane(new T.Vector3(-1, 0, 0), 1e9);
    this.orbit = { target: new T.Vector3(), theta: 0.6, phi: 1.05, radius: 500, tTheta: 0.6, tPhi: 1.05, tRadius: 500, pan: new T.Vector3() };
    this.bindOrbit();
    var ro = new ResizeObserver(function () { var w = host.clientWidth, h = host.clientHeight; if (!w || !h) return; renderer.setSize(w, h); cam.aspect = w / h; cam.updateProjectionMatrix(); }); ro.observe(host);
    var tmp = new T.Vector3();
    var lastW = 0, lastH = 0;
    // Außer Sicht nicht rendern (vorher 60 Bilder/s im Hintergrund): Beobachter startet die Schleife neu.
    self._vis = true; self._raf = 0;
    if ('IntersectionObserver' in window) new IntersectionObserver(function (es) {
      self._vis = es[es.length - 1].isIntersecting;
      if (self._vis && !self._raf) self._raf = requestAnimationFrame(loop);
    }).observe(self.stage);
    var loop = function () {
      if (!self._vis) { self._raf = 0; return; }
      // Größe jedes Bild prüfen: ResizeObserver feuert nicht zuverlässig, wenn der Viewer
      // in einem noch unsichtbaren Bereich startet — dann bliebe die Zeichenfläche 0 × 0.
      var hw = host.clientWidth, hh = host.clientHeight;
      if (hw && hh && (hw !== lastW || hh !== lastH)) { lastW = hw; lastH = hh; renderer.setSize(hw, hh); cam.aspect = hw / hh; cam.updateProjectionMatrix(); self.applyViewOffset(); }
      var o = self.orbit; o.theta += (o.tTheta - o.theta) * 0.18; o.phi += (o.tPhi - o.phi) * 0.18; o.radius += (o.tRadius - o.radius) * 0.18;
      var sp = Math.sin(o.phi);
      cam.position.set(o.target.x + o.radius * sp * Math.cos(o.theta), o.target.y + o.radius * sp * Math.sin(o.theta), o.target.z + o.radius * Math.cos(o.phi));
      cam.lookAt(o.target); renderer.render(scene, cam);
      var w = host.clientWidth, h = host.clientHeight;
      self.labels.forEach(function (l) { tmp.copy(l.pos).project(cam); var vis = tmp.z < 1; l.el.style.display = vis ? 'block' : 'none'; if (vis) l.el.style.transform = 'translate(-50%,-50%) translate(' + ((tmp.x + 1) / 2 * w) + 'px,' + ((1 - tmp.y) / 2 * h) + 'px)'; });
      self._raf = requestAnimationFrame(loop);
    };
    loop();
  };

  /** Freie Fläche der Bühne: oben verdeckt die Werkzeugleiste, unten die Schnittleiste (Handy: ~100 px). */
  P.freeArea = function () {
    var st = this.stage.getBoundingClientRect(), bar = this.root.querySelector('.gw-sv-bar'), sec = this.root.querySelector('.gw-sv-section');
    var top = 0, bot = 0;
    if (bar && bar.offsetWidth) { var b = bar.getBoundingClientRect(); top = Math.max(0, b.bottom - st.top + 6); }
    if (sec && sec.offsetWidth) { var s = sec.getBoundingClientRect(); bot = Math.max(0, st.bottom - s.top + 6); }
    // Handy: beim Messen/Flächen die Ergebnistafel fest einplanen (nicht bei jedem Ein-/Ausblenden springen)
    var res = this.root.querySelector('.gw-sv-result');
    if (this.tool !== 'orbit' && res && st.width < 560) bot += ((!res.hidden && res.offsetHeight) || 110) + 8;
    return { top: top, bot: bot, w: st.width, h: st.height };
  };
  /** Bildmitte in die freie Fläche schieben, damit Leisten das Teil nicht verdecken (Picking rechnet mit). */
  P.applyViewOffset = function () {
    if (!this.cam) return; var a = this.freeArea(); if (!a.w || !a.h) return;
    var d = (a.top - a.bot) / 2;
    if (Math.abs(d) < 2) this.cam.clearViewOffset(); else this.cam.setViewOffset(a.w, a.h, 0, -d, a.w, a.h);
    this._free = a;
  };

  P.bindOrbit = function () {
    var el = this.renderer.domElement, self = this, pts = new Map(), pinchD = 0, downAt = null, moved = false, multi = false;
    el.style.touchAction = 'none'; el.style.cursor = 'grab';
    function two() { var a = Array.from(pts.values()); return Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y); }
    el.addEventListener('pointerdown', function (e) {
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY, b: e.button });
      if (pts.size >= 2) { multi = true; pinchD = two(); }             // Zwei-Finger-Geste: löst nie einen Mess-/Flächenklick aus
      else if (!multi) { downAt = { x: e.clientX, y: e.clientY }; moved = false; }
      el.setPointerCapture && el.setPointerCapture(e.pointerId);
    });
    el.addEventListener('pointermove', function (e) {
      var p = pts.get(e.pointerId);
      if (!p) { if (self.tool !== 'orbit') self.onHover(e); return; }
      if (Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) > 4) moved = true;
      var o = self.orbit;
      if (pts.size >= 2) {
        // zwei Finger: Abstand = Zoom, Bewegung der Fingermitte = Verschieben (sonst Ränder nach dem Zoomen unerreichbar)
        var a0 = Array.from(pts.values()), mx0 = (a0[0].x + a0[1].x) / 2, my0 = (a0[0].y + a0[1].y) / 2;
        p.x = e.clientX; p.y = e.clientY;
        var a1 = Array.from(pts.values()), mx1 = (a1[0].x + a1[1].x) / 2, my1 = (a1[0].y + a1[1].y) / 2;
        var hpx = el.clientHeight || 1, mm = 2 * o.radius * Math.tan(self.THREE.MathUtils.degToRad(self.cam.fov / 2)) / hpx;
        var rv = new self.THREE.Vector3(), uv = new self.THREE.Vector3();
        rv.setFromMatrixColumn(self.cam.matrixWorld, 0); uv.setFromMatrixColumn(self.cam.matrixWorld, 1);
        o.target.addScaledVector(rv, -(mx1 - mx0) * mm).addScaledVector(uv, (my1 - my0) * mm);
        var nd = two(); if (pinchD > 0 && nd > 0) o.tRadius = Math.max(o.minR, Math.min(o.maxR, o.tRadius * pinchD / nd)); pinchD = nd; return;
      }
      var dx = e.clientX - p.x, dy = e.clientY - p.y;
      if (p.b === 2 || e.shiftKey) { // Verschieben
        var s = o.radius * 0.0018; var right = new self.THREE.Vector3(), up = new self.THREE.Vector3();
        right.setFromMatrixColumn(self.cam.matrixWorld, 0); up.setFromMatrixColumn(self.cam.matrixWorld, 1);
        o.target.addScaledVector(right, -dx * s).addScaledVector(up, dy * s);
      } else { o.tTheta -= dx * 0.007; o.tPhi = Math.max(0.02, Math.min(Math.PI - 0.02, o.tPhi - dy * 0.007)); }
      p.x = e.clientX; p.y = e.clientY;
    });
    function up(e) {
      var was = pts.has(e.pointerId); pts.delete(e.pointerId); if (pts.size < 2) pinchD = 0;
      var eat = self._eatClick; if (pts.size === 0) self._eatClick = false;
      if (multi) { if (pts.size === 0) multi = false; return; }
      if (eat) return;
      if (was && !moved && self.tool !== 'orbit') self.onClick(e);
    }
    el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
    el.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    el.addEventListener('wheel', function (e) { e.preventDefault(); var o = self.orbit; o.tRadius = Math.max(o.minR, Math.min(o.maxR, o.tRadius * (1 + (e.deltaY > 0 ? 0.1 : -0.1)))); }, { passive: false });
  };

  P.setTool = function (t) {
    this.tool = t;
    this.root.querySelectorAll('[data-tool]').forEach(function (b) { var on = b.getAttribute('data-tool') === t; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    if (this.renderer) this.renderer.domElement.style.cursor = t === 'orbit' ? 'grab' : 'crosshair';
    if (t !== 'measure') this.hover = null;
    if (t !== 'face') { this.selFaces = []; this.hoverFace = -1; }
    this.drawMarks(); this.renderSel();   // renderSel aktualisiert auch Hinweiszeile und Ergebnistafel
    this.applyViewOffset();               // Handy: Platz für die Ergebnistafel beim Messen
  };

  /** Hinweiszeile unter der Werkzeugleiste: sagt in jedem Schritt, was der nächste Klick bewirkt. */
  P.updateHint = function () {
    var h = this.root.querySelector('.gw-sv-hint'); if (!h) return;
    if (!this.model) { h.hidden = true; return; }
    var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches, html;
    if (this.tool === 'measure') {
      var n = this.measurePts.length, p = this.measurePts[0], p2;
      if (n === 1) {
        // Text passend zur Art von Punkt 1 — genau so, wie measureCore rechnet
        var k = p.kind === 'kreis' ? 'C' : (p.seg && !p.curve && p.kind === 'kante') ? 'L' : (p.kind === 'fläche' && p.plane) ? 'E' : 'P';
        p2 = k === 'L' ? 'parallele Kante = senkrechter Abstand · schräge Kante oder Fläche = Winkel · Ecke oder Bohrung = Lot auf die Kante'
          : k === 'E' ? 'Ecke, Kante oder Bohrung = Lot auf die Fläche · parallele Fläche = Abstand · schräge Fläche = Winkel'
          : k === 'C' ? 'Kante = Bohrungsachse ↔ Kante, senkrecht · Fläche = Lot · zweite Bohrung = Achsabstand · Ecke = direkt'
          : 'Kante oder Fläche = Maß senkrecht dazu, gerade Linie · Ecke oder Bohrung = direkter Abstand';
      }
      html = n === 0 ? '<b>Punkt 1:</b> Bohrungsrand (fängt die Mitte), Ecke, Kante oder Fläche anklicken'
        : n === 1 ? '<b>Punkt 2:</b> ' + p2
        : '<b>Fertig.</b> Nächster Klick startet eine neue Messung · Klick auf einen Messpunkt entfernt ihn';
      html += '<span class="gw-sv-legend"><span><i class="k1"></i>Bohrungsmitte</span><span><i class="k2"></i>Ecke</span><span><i class="k3"></i>Kante</span></span>';
    } else if (this.tool === 'face') {
      var k = this.selFaces.length;
      html = k === 0 ? '<b>Fläche anklicken:</b> zeigt Bohrungs-Ø, Radius, Fase oder Neigungswinkel'
        : k === 1 ? '<b>Zweite Fläche anklicken:</b> Abstand oder Winkel zwischen beiden Flächen'
        : '<b>Fertig.</b> Nächster Klick startet neu · Esc löscht die Auswahl';
    } else {
      html = coarse ? 'Ein Finger dreht · zwei Finger zoomen und verschieben · zum Messen oben den Knopf mit dem Lineal wählen'
        : 'Ziehen dreht · rechte Maustaste oder Shift verschiebt · Mausrad zoomt · zum Messen oben den Knopf mit dem Lineal wählen';
    }
    h.hidden = false; h.innerHTML = html;
  };

  P.fit = function (dir) {
    var T = this.THREE, m = this.model, o = this.orbit; if (!m) return;
    var size = m.bbox.getSize(new T.Vector3()), center = m.bbox.getCenter(new T.Vector3());
    var maxDim = Math.max(size.x, size.y, size.z), dist = (maxDim / (2 * Math.tan(T.MathUtils.degToRad(this.cam.fov / 2)))) * 1.35;
    var fa = this._free; if (fa && fa.h) dist *= Math.min(1.8, fa.h / Math.max(fa.h * 0.45, fa.h - fa.top - fa.bot));   // nur in die freie Fläche einpassen
    // auch die Breite einpassen (Handy hochkant: sonst stößt z. B. die Lochplatte seitlich an)
    var hH = Math.atan(Math.tan(T.MathUtils.degToRad(this.cam.fov / 2)) * (this.cam.aspect || 1));
    dist = Math.max(dist, size.length() / (2 * Math.tan(hH)) * 1.08);
    o.target.copy(center); o.tRadius = dist; o.minR = dist * 0.05; o.maxR = dist * 8;
    this.cam.near = dist / 200; this.cam.far = dist * 50; this.cam.updateProjectionMatrix();
    var d = dir || new T.Vector3(0.7, -1, 0.75); d = d.clone().normalize();
    o.tPhi = Math.acos(Math.max(-1, Math.min(1, d.z))); o.tTheta = Math.atan2(d.y, d.x);
    if (Math.abs(d.x) < 1e-6 && Math.abs(d.y) < 1e-6) o.tTheta = -Math.PI / 2;   // Draufsicht/Untersicht: X waagerecht, Y nach oben
    // kürzester Drehweg: nach langem Drehen ist theta weit über 2π — sonst wirbelt die Kamera mehrfach herum
    var dT = o.tTheta - o.theta; dT = ((dT + Math.PI) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI) - Math.PI; o.tTheta = o.theta + dT;
    if (o.tPhi < 0.02) o.tPhi = 0.02; if (o.tPhi > Math.PI - 0.02) o.tPhi = Math.PI - 0.02;
  };
  P.view = function (v) {
    var T = this.THREE; if (!T) return;
    var dirs = { x: [1, 0, 0], '-x': [-1, 0, 0], y: [0, -1, 0], '-y': [0, 1, 0], z: [0, 0, 1], '-z': [0, 0, -1] };
    if (v === 'flip') { this.rotatePart('x', Math.PI); return; }      // Teil umdrehen (Unterseite nach oben)
    if (v === 'tilt') { this.rotatePart('x', Math.PI / 2); return; }   // Teil um 90° kippen
    if (v === 'turn') { this.rotatePart('z', Math.PI / 2); return; }   // Teil um 90° auf dem Tisch drehen
    this.fit(v === 'iso' ? null : new T.Vector3().fromArray(dirs[v] || [0, 0, 1]));
  };

  P.rotatePart = function (axis, ang) {
    var T = this.THREE, m = this.model; if (!m) return;
    var c = m.bbox.getCenter(new T.Vector3());
    var R = new T.Matrix4(); if (axis === 'x') R.makeRotationX(ang); else if (axis === 'y') R.makeRotationY(ang); else R.makeRotationZ(ang);
    var M = new T.Matrix4().makeTranslation(c.x, c.y, c.z).multiply(R).multiply(new T.Matrix4().makeTranslation(-c.x, -c.y, -c.z));
    var bbox = new T.Box3();
    m.geoms.forEach(function (g) { g.applyMatrix4(M); g.computeBoundingBox(); g.computeBoundingSphere(); bbox.union(g.boundingBox); });
    m.bbox = bbox;
    m.info = m.faces.map(function (_, gi) { return analyzeFace(T, m, gi); });
    m.holes = groupHoles(T, m); m.circles = circlesFrom(T, m);
    var basis = -1, bestA = 0; m.info.forEach(function (f, i) { if (f.type === 'plane' && f.area > bestA) { bestA = f.area; basis = i; } }); m.baseFace = basis;
    var o = this.orbit, keep = { theta: o.tTheta, phi: o.tPhi, r: o.tRadius };
    this.selFaces = []; this.measurePts = []; this.measAlt = false; this.hover = null; this.hiliteD = null;
    this.buildScene();
    o.tTheta = keep.theta; o.tPhi = keep.phi; o.tRadius = keep.r;   // Blickrichtung bleibt — das Teil hat sich bewegt
    this.renderInfo(); this.renderHoles(); this.renderSel(); this.renderMeas(); this.drawDims(); this.drawMarks(); this.applySection();
  };

  P.buildScene = function () {
    var T = this.THREE, m = this.model, g = this.groups, self = this;
    Object.keys(g).forEach(function (k) { g[k].clear(); });
    this.meshes = []; this.mats = []; this.edgeLines = []; this.clearLabels();
    var diag = m.bbox.getSize(new T.Vector3()).length();
    m.geoms.forEach(function (geo) {
      var mat = new T.MeshStandardMaterial({ color: 0x9fb2c7, metalness: 0.25, roughness: 0.5, side: T.DoubleSide, clippingPlanes: [self.plane], polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1 });
      var mesh = new T.Mesh(geo, mat); mesh.renderOrder = 2; g.model.add(mesh); self.meshes.push(mesh); self.mats.push(mat);
      var edges = new T.LineSegments(faceBoundaryEdges(T, m, self.meshes.length - 1), new T.LineBasicMaterial({ color: 0x1b2331, clippingPlanes: [self.plane] })); edges.renderOrder = 3; g.model.add(edges); self.edgeLines.push(edges);
      var mk = function (side, op) { return new T.MeshBasicMaterial({ side: side, clippingPlanes: [self.plane], depthWrite: false, depthTest: false, colorWrite: false, stencilWrite: true, stencilFunc: T.AlwaysStencilFunc, stencilFail: op, stencilZFail: op, stencilZPass: op }); };
      var back = new T.Mesh(geo, mk(T.BackSide, T.IncrementWrapStencilOp)); back.renderOrder = 1; back.visible = false;
      var front = new T.Mesh(geo, mk(T.FrontSide, T.DecrementWrapStencilOp)); front.renderOrder = 1; front.visible = false;
      g.cap.add(back, front);
    });
    var capMat = new T.MeshStandardMaterial({ color: 0xc9873a, emissive: 0x3a2206, metalness: 0.1, roughness: 0.7, stencilWrite: true, stencilRef: 0, stencilFunc: T.NotEqualStencilFunc, stencilFail: T.ReplaceStencilOp, stencilZFail: T.ReplaceStencilOp, stencilZPass: T.ReplaceStencilOp });
    var cap = new T.Mesh(new T.PlaneGeometry(diag * 2.2, diag * 2.2), capMat); cap.name = 'cap'; cap.renderOrder = 1.5; cap.visible = false; cap.onAfterRender = function (r) { r.clearStencil(); }; g.cap.add(cap);
    var grid = new T.GridHelper(Math.ceil(diag * 1.6 / 50) * 50, Math.ceil(diag * 1.6 / 50), 0x2a3444, 0x1a2230); grid.rotation.x = Math.PI / 2;
    grid.position.set((m.bbox.min.x + m.bbox.max.x) / 2, (m.bbox.min.y + m.bbox.max.y) / 2, m.bbox.min.z - 0.5); g.helper.add(grid);
    this.edgeModel = buildEdgeModel(T, this.edgeLines);
    this.applyDisplay(); this.fit();
  };

  P.applyDisplay = function () {
    var self = this; if (!this.mats) return;
    this.mats.forEach(function (mt) { mt.transparent = self.ghost; mt.opacity = self.ghost ? 0.32 : 1; mt.depthWrite = !self.ghost; mt.needsUpdate = true; });
    this.edgeLines.forEach(function (e) { e.visible = self.showEdges; });
  };

  // ── Beschriftungen ──
  P.clearLabels = function (kind) { var keep = []; this.labels.forEach(function (l) { if (kind && l.kind !== kind) keep.push(l); else l.el.remove(); }); this.labels = keep; };
  P.addLabel = function (pos, text, kind, cls) { var el = document.createElement('div'); el.className = 'gw-sv-lbl' + (cls ? ' ' + cls : ''); el.textContent = text; this.overlay.appendChild(el); this.labels.push({ el: el, pos: pos.clone(), kind: kind }); };
  P.drawDims = function () {
    var T = this.THREE, m = this.model; if (!m) return; this.clearLabels('dim');
    var old = this.groups.helper.getObjectByName('dims'); if (old) this.groups.helper.remove(old);
    if (!this.showDims) return;
    var box = new T.Box3Helper(m.bbox, new T.Color(0x3b82f6)); box.name = 'dims'; box.material.transparent = true; box.material.opacity = 0.55; this.groups.helper.add(box);
    var s = m.bbox.getSize(new T.Vector3()), mn = m.bbox.min, mx = m.bbox.max;
    this.addLabel(new T.Vector3((mn.x + mx.x) / 2, mn.y, mn.z), 'X ' + fmt(s.x), 'dim', 'is-dim');
    this.addLabel(new T.Vector3(mx.x, (mn.y + mx.y) / 2, mn.z), 'Y ' + fmt(s.y), 'dim', 'is-dim');
    this.addLabel(new T.Vector3(mx.x, mx.y, (mn.z + mx.z) / 2), 'Z ' + fmt(s.z), 'dim', 'is-dim');
  };

  // ── Schnitt ──
  P.setSection = function (ax) {
    this.sec.axis = ax; var self = this;
    this.root.querySelectorAll('[data-axis]').forEach(function (b) { var on = b.getAttribute('data-axis') === ax; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
    this.root.querySelector('.gw-sv-secoff').hidden = !ax;
    var bar = this.root.querySelector('.gw-sv-section'); if (bar) bar.classList.toggle('is-active', !!ax);   // schmal: Regler erst nach Achswahl
    this.applyViewOffset();
    this.applySection();
  };
  P.applySection = function () {
    var T = this.THREE, m = this.model; var lbl = this.root.querySelector('.gw-sv-sec-v');
    if (!m) { lbl.textContent = 'Achse wählen'; return; }
    var cap = this.groups.cap.getObjectByName('cap'), s = this.sec;
    if (!s.axis) { this.plane.set(new T.Vector3(-1, 0, 0), 1e9); this.groups.cap.children.forEach(function (c) { c.visible = false; }); lbl.textContent = 'Achse wählen'; return; }
    var n = new T.Vector3(s.axis === 'x' ? 1 : 0, s.axis === 'y' ? 1 : 0, s.axis === 'z' ? 1 : 0).multiplyScalar(s.flip ? 1 : -1);
    var lo = m.bbox.min[s.axis], hi = m.bbox.max[s.axis], v = lo + (hi - lo) * s.pos;
    this.plane.set(n, s.flip ? -v : v);
    this.groups.cap.children.forEach(function (c) { c.visible = true; });
    // Deckel zeigt zur weggeschnittenen Seite (dort steht die Kamera); n zeigt zum behaltenen Material.
    // Vorher lookAt(c + n): Rückseite zur Kamera → FrontSide-Material unsichtbar → „hohles Netz".
    var c = m.bbox.getCenter(new T.Vector3()); c[s.axis] = v; cap.position.copy(c); cap.lookAt(c.clone().sub(n));
    lbl.textContent = s.axis.toUpperCase() + ' = ' + fmt(v);
  };

  // ── Picking / Fang ──
  P.pick = function (ev) {
    var T = this.THREE, m = this.model; if (!m) return null;
    var rect = this.renderer.domElement.getBoundingClientRect();
    var ndc = new T.Vector2(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
    var rc = new T.Raycaster(); rc.setFromCamera(ndc, this.cam);
    var hits = rc.intersectObjects(this.meshes, false);
    for (var i = 0; i < hits.length; i++) {
      var h = hits[i]; if (this.sec.axis && this.plane.distanceToPoint(h.point) < 0) continue;
      // Klick auf die Schnittfläche (Innenseite zuerst getroffen): keine verdeckte Fläche dahinter auswählen
      if (this.sec.axis && h.face && h.face.normal.clone().transformDirection(h.object.matrixWorld).dot(rc.ray.direction) > 0) {
        var cpp = rc.ray.intersectPlane(this.plane, new T.Vector3());
        return cpp ? { point: cpp, face: -1, mmPerPx: 0, rect: rect } : null;
      }
      var mi = this.meshes.indexOf(h.object); var gi = h.faceIndex != null ? m.triToFace[mi][h.faceIndex] : -1;
      // Bohrungsfang: nahe einer Bohrungsachse → Bohrungswand statt Planfläche
      var camDist = this.cam.position.distanceTo(h.point), mmPerPx = (2 * camDist * Math.tan(T.MathUtils.degToRad(this.cam.fov / 2))) / rect.height, tol = 8 * mmPerPx;
      var best = null, q = new T.Vector3();
      m.holes.list.forEach(function (hole) { var f = m.info[hole.faces[0]]; q.copy(h.point).sub(f.axisPoint); var along = q.dot(f.axis); var radial = q.addScaledVector(f.axis, -along).length(); var d = Math.max(0, radial - hole.d / 2); if (d < tol && (!best || d < best.d)) best = { gi: hole.faces[0], d: d }; });
      if (best) gi = best.gi;
      return { point: h.point.clone(), face: gi, mmPerPx: mmPerPx, rect: rect };
    }
    return null;
  };
  P.snap = function (ev) {
    var T = this.THREE, m = this.model, self = this; if (!m) return null;
    var el = this.renderer.domElement, rect = el.getBoundingClientRect(); if (!rect.width) return null;
    var mx = ev.clientX - rect.left, my = ev.clientY - rect.top;
    var ndc = new T.Vector2((mx / rect.width) * 2 - 1, -(my / rect.height) * 2 + 1);
    var ray = new T.Raycaster(); ray.setFromCamera(ndc, this.cam);
    var sec = this.sec.axis ? this.plane : null;
    // Bei aktivem Schnitt: trifft der Strahl zuerst eine Innenseite (Rückseite), liegt davor die Schnittfläche —
    // dann zählt der Punkt auf der Schnittebene, nicht die verdeckte Geometrie dahinter.
    var firstHit = function (rc) {
      var hs = rc.intersectObjects(self.meshes, false);
      for (var i = 0; i < hs.length; i++) if (!sec || sec.distanceToPoint(hs[i].point) >= 0) {
        var h = hs[i];
        if (sec && h.face && h.face.normal.clone().transformDirection(h.object.matrixWorld).dot(rc.ray.direction) > 0) {
          var cp = rc.ray.intersectPlane(sec, new T.Vector3());
          if (cp) return { point: cp, distance: cp.distanceTo(rc.ray.origin), cap: true };
        }
        return h;
      }
      return null;
    };
    var hit = firstHit(ray);
    var toPx = function (v) { var pr = v.clone().project(self.cam); return new T.Vector2(((pr.x + 1) / 2) * rect.width, ((1 - pr.y) / 2) * rect.height); };
    var camPos = this.cam.position, occ = new T.Raycaster();
    var visible = function (p) {
      if (sec && sec.distanceToPoint(p) < -1e-3) return false;
      var dv = p.clone().sub(camPos), dist = dv.length(); occ.set(camPos, dv.normalize());
      var h = firstHit(occ); return !h || h.distance > dist - Math.max(0.05, dist * 2e-4);
    };
    var best = null, bestScore = Infinity;
    var consider = function (pt, kind, seg, bonus, props) {
      var px = toPx(pt).distanceTo(new T.Vector2(mx, my)); if (px > 12) return;
      var sc = px - bonus; if (sc >= bestScore || !visible(pt)) return;
      bestScore = sc; best = { point: pt.clone(), kind: kind, seg: seg };
      if (props) Object.keys(props).forEach(function (k) { best[k] = props[k]; });
    };
    // Kreismittelpunkt, wenn der Kreisrand getroffen ist. Bewertet in Bildschirmpixeln wie die Kanten
    // (mit 5 px Vorsprung) statt absolutem Vorrang: bei kleinem Maßstab (Handy, ganzes Teil im Bild)
    // sind 12 px leicht 9 mm — sonst gewinnt die Bohrung auch, wenn eine Kante gemeint ist.
    if (hit) {
      var camDist = camPos.distanceTo(hit.point), mmPerPx = (2 * camDist * Math.tan(T.MathUtils.degToRad(this.cam.fov / 2))) / rect.height;
      m.circles.forEach(function (ci) {
        var d = hit.point.clone().sub(ci.c), along = d.dot(ci.ax), radial = d.addScaledVector(ci.ax, -along).length();
        var px = Math.max(Math.abs(radial - ci.r), Math.abs(along)) / mmPerPx; if (px > 12) return;
        var sc = px - 5; if (sc < bestScore) { bestScore = sc; best = { point: ci.c.clone(), kind: 'kreis', seg: null, circle: ci }; }
      });
    }
    // Kanten: Abstand Maus ↔ projizierte Kante; 3D-Punkt = nächster Punkt Sichtstrahl ↔ Kante
    var o = ray.ray.origin, dr = ray.ray.direction, m2 = new T.Vector2(mx, my);
    (this.edgeModel || []).forEach(function (e) {
      var pa = toPx(e.a), pb = toPx(e.b), ab2 = pb.clone().sub(pa), l2 = ab2.lengthSq();
      var u2 = l2 > 0 ? Math.max(0, Math.min(1, m2.clone().sub(pa).dot(ab2) / l2)) : 0;
      if (pa.clone().addScaledVector(ab2, u2).distanceTo(m2) > 12) return;
      var ab = e.b.clone().sub(e.a), w0 = e.a.clone().sub(o), A = ab.dot(ab), B = ab.dot(dr), C = dr.dot(dr), D = ab.dot(w0), E = dr.dot(w0);
      var den = A * C - B * B, u = den > 1e-12 ? (B * E - C * D) / den : u2; u = Math.max(0, Math.min(1, u));
      var q = e.a.clone().addScaledVector(ab, u), seg = [e.a.clone(), e.b.clone()];
      if (e.aCorner) consider(e.a, 'ecke', seg, 6);
      if (e.bCorner) consider(e.b, 'ecke', seg, 6);
      if (e.real) consider(e.a.clone().add(e.b).multiplyScalar(0.5), 'mitte', seg, 3);
      consider(q, 'kante', seg, 0, { curve: !!e.curve });
    });
    if (!best && hit && hit.cap) {   // Punkt auf der Schnittfläche: Ebene = Schnittebene
      return { point: hit.point.clone(), kind: 'fläche', seg: null, face: -1, plane: { n: this.plane.normal.clone().normalize(), p: hit.point.clone() } };
    }
    if (!best && hit) {
      // Flächenpunkt; bei einer Planfläche merken wir die Ebene → Messung senkrecht zur Fläche möglich
      var hmi = this.meshes.indexOf(hit.object), hgi = hit.faceIndex != null && hmi >= 0 ? m.triToFace[hmi][hit.faceIndex] : -1, hfi = hgi >= 0 ? m.info[hgi] : null;
      best = { point: hit.point.clone(), kind: 'fläche', seg: null, face: hgi };
      if (hfi && hfi.type === 'plane' && hfi.normal) best.plane = { n: hfi.normal.clone().normalize(), p: hit.point.clone() };
    }
    return best;
  };
  P.onHover = function (ev) {
    if (this.tool === 'measure') { this.hover = this.snap(ev); this.drawMarks(); }
    else if (this.tool === 'face') { var h = this.pick(ev); var gi = h ? h.face : -1; if (gi !== this.hoverFace) { this.hoverFace = gi; this.drawMarks(); } }
  };
  P.onClick = function (ev) {
    var self = this;
    if (this.tool === 'measure') {
      var rect = this.renderer.domElement.getBoundingClientRect();
      var scr = function (v) { var pr = v.clone().project(self.cam); return { x: ((pr.x + 1) / 2) * rect.width, y: ((1 - pr.y) / 2) * rect.height }; };
      var sn = this.snap(ev), mx = ev.clientX - rect.left, my = ev.clientY - rect.top;
      // Entfernen nur, wenn der Klick wirklich auf dem Messpunkt landet (der Fang trifft dieselbe Stelle).
      // Fängt der Klick etwas anderes in der Nähe (z. B. die Kante neben der Bohrung), wird ein neuer Punkt gesetzt.
      var near = this.measurePts.findIndex(function (p) {
        var a = scr(p.point); if (Math.hypot(a.x - mx, a.y - my) >= 14) return false;
        if (!sn) return true; var b = scr(sn.point); return Math.hypot(a.x - b.x, a.y - b.y) < 6;
      });
      if (near >= 0) { this.measurePts.splice(near, 1); }
      else { if (!sn) return; if (this.measurePts.length >= 2) this.measurePts = [sn]; else this.measurePts.push(sn); }
      this.measAlt = false; this.hover = null;
      this.drawMarks(); this.renderMeas();
    } else if (this.tool === 'face') {
      var h = this.pick(ev);
      if (!h || h.face < 0) { this.selFaces = []; } else { this.hiliteD = null; if (this.selFaces.length >= 2) this.selFaces = [h.face]; else if (this.selFaces.indexOf(h.face) < 0) this.selFaces.push(h.face); }
      this.drawMarks(); this.renderSel(); this.renderHoles();
    }
  };

  // ── Markierungen ──
  P.faceGeometry = function (gis) {
    var T = this.THREE, m = this.model, byMesh = {};
    gis.forEach(function (gi) { var fr = m.faces[gi]; if (!fr) return; (byMesh[fr.mesh] = byMesh[fr.mesh] || []).push(gi); });
    return Object.keys(byMesh).map(function (mi) {
      var g = m.geoms[mi], idx = g.getIndex(), tris = [];
      byMesh[mi].forEach(function (gi) { var fr = m.faces[gi]; for (var t = fr.first; t <= fr.last; t++) for (var k = 0; k < 3; k++) tris.push(idx ? idx.getX(3 * t + k) : 3 * t + k); });
      var hg = new T.BufferGeometry(); hg.setAttribute('position', g.getAttribute('position')); hg.setIndex(tris); return hg;
    });
  };
  P.drawMarks = function () {
    var T = this.THREE, m = this.model, g = this.groups.mark, self = this; if (!m) return;
    g.clear(); this.clearLabels('mark');
    var paint = function (gis, color, opacity) { self.faceGeometry(gis).forEach(function (hg) { var mesh = new T.Mesh(hg, new T.MeshBasicMaterial({ color: color, transparent: true, opacity: opacity, side: T.DoubleSide, clippingPlanes: [self.plane], polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 })); mesh.renderOrder = 4; g.add(mesh); }); };
    if (this.tool === 'face' && this.hoverFace >= 0 && this.selFaces.indexOf(this.hoverFace) < 0) paint([this.hoverFace], 0xfbbf24, 0.35);
    if (this.selFaces.length) paint(this.selFaces, 0xf97316, 0.7);
    if (this.hiliteD != null) { var gis = []; m.holes.list.forEach(function (h) { if (h.d === self.hiliteD) gis.push.apply(gis, h.faces); }); paint(gis, 0x22d3ee, 0.75); }
    var s = m.bbox.getSize(new T.Vector3()).length();
    if (this.hover && this.tool === 'measure') {
      var hv = this.hover;
      if (hv.seg) { var ln = new T.Line(new T.BufferGeometry().setFromPoints(hv.seg), new T.LineBasicMaterial({ color: 0x4ade80, depthTest: false })); ln.renderOrder = 8; g.add(ln); }
      if (hv.kind === 'kreis') { var ring = new T.Mesh(new T.RingGeometry(hv.circle.r * 0.97, hv.circle.r * 1.03, 48), new T.MeshBasicMaterial({ color: 0x38bdf8, side: T.DoubleSide, depthTest: false, transparent: true, opacity: 0.9 })); ring.position.copy(hv.point); ring.lookAt(hv.point.clone().add(hv.circle.ax)); ring.renderOrder = 8; g.add(ring); }
      var col = hv.kind === 'kreis' ? 0x38bdf8 : hv.kind === 'ecke' ? 0xfbbf24 : hv.kind === 'mitte' ? 0x7dd3fc : hv.kind === 'kante' ? 0x4ade80 : 0x94a3b8;
      var sp = new T.Mesh(new T.SphereGeometry(Math.max(0.7, s * 0.004), 16, 12), new T.MeshBasicMaterial({ color: col, depthTest: false, transparent: true, opacity: 0.85 })); sp.position.copy(hv.point); sp.renderOrder = 9; g.add(sp);
      this.addLabel(hv.point, hv.kind === 'kreis' ? circLabel(hv.circle) : hv.kind === 'ecke' ? 'Ecke' : hv.kind === 'mitte' ? 'Kantenmitte' : hv.kind === 'kante' ? (hv.curve ? 'Bogenkante' : 'Kante') : hv.plane ? 'Planfläche' : 'Fläche', 'mark', 'is-snap');
    }
    this.measurePts.forEach(function (p) { var sp2 = new T.Mesh(new T.SphereGeometry(Math.max(0.6, s * 0.0035), 16, 12), new T.MeshBasicMaterial({ color: p.kind === 'kreis' ? 0x38bdf8 : 0x22c55e, depthTest: false })); sp2.position.copy(p.point); sp2.renderOrder = 9; g.add(sp2); if (p.seg) { var l2 = new T.Line(new T.BufferGeometry().setFromPoints(p.seg), new T.LineBasicMaterial({ color: 0x22c55e, depthTest: false, transparent: true, opacity: 0.7 })); l2.renderOrder = 8; g.add(l2); } });
    var drawMeasure = function (r, preview) {
      var line = new T.Line(new T.BufferGeometry().setFromPoints([r.a, r.b]), new T.LineBasicMaterial({ color: 0x22c55e, depthTest: false, transparent: true, opacity: preview ? 0.55 : 1 })); line.renderOrder = 9; g.add(line);
      // gestrichelt: Verlängerung der Kante bis zum Lotfußpunkt bzw. Bohrungsachse bis zur Messebene
      (r.ext || []).forEach(function (e) {
        var dl = new T.Line(new T.BufferGeometry().setFromPoints(e), new T.LineDashedMaterial({ color: 0x86efac, dashSize: s * 0.008, gapSize: s * 0.006, depthTest: false, transparent: true, opacity: preview ? 0.45 : 0.85 }));
        dl.computeLineDistances(); dl.renderOrder = 8; g.add(dl);
      });
      if (r.tag && !preview) { var ft = new T.Mesh(new T.SphereGeometry(Math.max(0.45, s * 0.0026), 12, 10), new T.MeshBasicMaterial({ color: 0xecfdf5, depthTest: false })); ft.position.copy(r.b); ft.renderOrder = 9; g.add(ft); }
      var txt = r.angle != null ? fmt(r.angle, 2) + '°' : fmt(r.d, 3) + ' mm' + (r.tag ? ' · ' + r.tag : '');
      self.addLabel(r.a.clone().add(r.b).multiplyScalar(0.5), txt, 'mark', preview ? 'is-meas is-prev' : 'is-meas');
    };
    if (this.measurePts.length === 2) drawMeasure(evalMeasure(T, this.measurePts[0], this.measurePts[1], this.measAlt), false);
    else if (this.measurePts.length === 1 && this.hover && this.tool === 'measure') {
      // Vorschau: zeigt schon beim Zielen, ob die Linie gerade (senkrecht) oder direkt verläuft
      var pv = evalMeasure(T, this.measurePts[0], this.hover, false);
      if (pv.angle != null || pv.d > 1e-4) drawMeasure(pv, true);
    }
  };
  // ── Messlogik ──
  // Gerade Kante = Linie (unendlich verlängert) · Planfläche = Ebene · Bohrungsmitte = Punkt MIT Achse ·
  // Ecke, Kantenmitte, Bogenkante, gekrümmte Fläche = Punkt. Gegen Linie und Ebene wird senkrecht (Lot)
  // gemessen — so entsteht das Zeichnungsmaß und nicht die schräge Verbindungslinie, die länger ist.
  // Bohrung ↔ Kante misst Achse ↔ Kante: gleiches Ergebnis, egal an welchem Bohrungsrand der Punkt sitzt.
  function footOn(q, o, d) { return o.clone().addScaledVector(d, q.clone().sub(o).dot(d)); }
  function onPlane(q, pl) { return q.clone().addScaledVector(pl.n, -q.clone().sub(pl.p).dot(pl.n)); }
  function closestLines(p1, d1, p2, d2) {   // nächste Punkte zweier nicht paralleler Geraden
    var w = p1.clone().sub(p2), a = d1.dot(d1), b = d1.dot(d2), c = d2.dot(d2), d = d1.dot(w), e = d2.dot(w), den = a * c - b * b;
    var s = den > 1e-12 ? (b * e - c * d) / den : 0, t = den > 1e-12 ? (a * e - b * d) / den : e / c;
    return { a: p1.clone().addScaledVector(d1, s), b: p2.clone().addScaledVector(d2, t) };
  }
  function withExt(r, seg, f) {             // Fußpunkt außerhalb der Kante → gestrichelte Verlängerung
    var d = seg[1].clone().sub(seg[0]), L = d.length(); if (L < 1e-9) return r; d.divideScalar(L);
    var t = f.clone().sub(seg[0]).dot(d);
    if (t < -1e-3) r.ext.push([seg[0].clone(), f.clone()]); else if (t > L + 1e-3) r.ext.push([seg[1].clone(), f.clone()]);
    return r;
  }
  function circName(ci) { return !ci ? 'Kreismitte' : ci.sweep != null && ci.sweep <= 190 ? 'Radiusmitte' : ci.hole === false ? 'Kreismitte' : 'Bohrungsmitte'; }
  function circLabel(ci) { return circName(ci) + (ci && ci.sweep != null && ci.sweep <= 190 ? ' R ' + fmt(ci.r) : ' Ø ' + fmt(ci.r * 2)); }
  function isPar(c) { return Math.sqrt(Math.max(0, 1 - c * c)) < 0.002; }   // parallel = weniger als 0,11° Abweichung
  function nameOf(p) { return p.kind === 'kreis' ? circName(p.circle) : p.kind === 'ecke' ? 'Ecke' : p.kind === 'mitte' ? 'Kantenmitte' : p.kind === 'kante' ? (p.curve ? 'Bogenkante' : 'Kante') : p.plane ? 'Planfläche' : 'Flächenpunkt'; }
  /** mode: 'auto' (Standard) · 'line' (Kantenmitte als Kante behandeln) · 'direct' (immer Punkt ↔ Punkt) */
  function measureCore(T, p1, p2, mode) {
    var typ = function (p) {
      if (mode === 'direct') return 'P';
      if (p.seg && !p.curve && (p.kind === 'kante' || (mode === 'line' && p.kind === 'mitte'))) return 'L';
      if (p.kind === 'fläche' && p.plane) return 'E';
      return 'P';
    };
    var dir = function (p) { return p.seg[1].clone().sub(p.seg[0]).normalize(); };
    var deg = function (c) { return T.MathUtils.radToDeg(Math.acos(Math.min(1, Math.abs(c)))); };
    var circ = function (p) { return mode !== 'direct' && p.kind === 'kreis' && p.circle; };
    var out = function (a, b, label, tag) {
      var v = b.clone().sub(a);
      return { a: a, b: b, d: v.length(), label: label, tag: tag || '', ext: [], delta: 'ΔX ' + fmt(Math.abs(v.x)) + ' · ΔY ' + fmt(Math.abs(v.y)) + ' · ΔZ ' + fmt(Math.abs(v.z)) };
    };
    var angle = function (ang, label) { var r = out(p1.point.clone(), p2.point.clone(), label + ' (Gegenwinkel ' + fmt(180 - ang, 2) + '°)'); r.angle = ang; return r; };
    var t1 = typ(p1), t2 = typ(p2), r;
    var ptName = function (p) { return p.kind === 'fläche' ? 'Flächenpunkt' : nameOf(p); };   // Klickpunkt, kein Flächenmaß
    // Kante ↔ Kante: parallel → senkrechter Abstand, sonst Winkel (kollinear → unten Punkt ↔ Punkt)
    if (t1 === 'L' && t2 === 'L') {
      var d1 = dir(p1), c1 = Math.abs(d1.dot(dir(p2)));
      if (!isPar(c1)) return angle(deg(c1), 'Winkel zwischen den Kanten');
      var f1 = footOn(p2.point, p1.seg[0], d1);
      if (f1.distanceTo(p2.point) > 1e-3) return withExt(out(f1, p2.point.clone(), 'Kante ↔ Kante, senkrecht gemessen', 'senkrecht'), p1.seg, f1);
    }
    // Fläche ↔ Fläche: parallel → Abstand, sonst Winkel (gleiche Ebene → unten Punkt ↔ Punkt)
    if (t1 === 'E' && t2 === 'E') {
      var c2 = Math.abs(p1.plane.n.dot(p2.plane.n));
      if (!isPar(c2)) return angle(deg(c2), 'Winkel zwischen den Flächen');
      var f2 = onPlane(p2.point, p1.plane);
      if (f2.distanceTo(p2.point) > 1e-3) return out(f2, p2.point.clone(), 'Fläche ↔ Fläche (parallel), senkrecht gemessen', 'senkrecht');
    }
    // Kante ↔ Fläche: parallel → Abstand, sonst Winkel der Kante zur Fläche
    if ((t1 === 'L' && t2 === 'E') || (t1 === 'E' && t2 === 'L')) {
      var pe = t1 === 'E' ? p1 : p2, pl = t1 === 'L' ? p1 : p2, s3 = Math.abs(dir(pl).dot(pe.plane.n));
      if (s3 > 0.002) return angle(90 - deg(s3), 'Winkel Kante ↔ Fläche');
      var f3 = onPlane(pl.point, pe.plane);
      if (f3.distanceTo(pl.point) > 1e-3) return out(pl.point.clone(), f3, 'Kante ↔ Fläche (parallel), senkrecht gemessen', 'senkrecht');
    }
    // Bohrungsmitte ↔ Kante: Achse ↔ Kante
    if ((circ(p1) && t2 === 'L') || (t1 === 'L' && circ(p2))) {
      var ci = circ(p1) ? p1 : p2, le = circ(p1) ? p2 : p1, ax = ci.circle.ax.clone().normalize(), de = dir(le);
      var f4 = footOn(ci.point, le.seg[0], de);
      if (isPar(ax.dot(de))) { r = out(ci.point.clone(), f4, nameOf(ci) + ' ↔ Kante, senkrecht gemessen', 'senkrecht'); return withExt(r, le.seg, f4); }
      var cl = closestLines(ci.point, ax, le.seg[0], de);
      // Achse fast parallel zur Kante: der gemeinsame Lotpunkt läge weit außerhalb des Teils → Lot von der Mitte
      if (cl.a.distanceTo(ci.point) > Math.max(ci.point.distanceTo(le.point), le.seg[0].distanceTo(le.seg[1]))) {
        r = out(ci.point.clone(), f4, nameOf(ci) + ' ↔ Kante, senkrecht gemessen', 'senkrecht'); return withExt(r, le.seg, f4);
      }
      var offAxis = cl.a.distanceTo(ci.point) > 1e-3;
      r = out(cl.a, cl.b, (offAxis ? (circName(ci.circle) === 'Bohrungsmitte' ? 'Bohrungsachse' : 'Achse') : nameOf(ci)) + ' ↔ Kante, senkrecht gemessen', 'senkrecht'); withExt(r, le.seg, cl.b);
      if (offAxis) r.ext.push([ci.point.clone(), cl.a.clone()]);
      return r;
    }
    // Bohrung ↔ Bohrung mit parallelen Achsen: Achsabstand (koaxial → unten Mittelpunktabstand = Tiefe)
    if (circ(p1) && circ(p2) && isPar(p1.circle.ax.clone().normalize().dot(p2.circle.ax.clone().normalize()))) {
      var f5 = footOn(p2.point, p1.point, p1.circle.ax.clone().normalize());
      if (f5.distanceTo(p2.point) > 1e-3) {
        var sh = function (p) { return { Bohrungsmitte: 'Bohrung', Radiusmitte: 'Radius', Kreismitte: 'Kreis' }[nameOf(p)] || nameOf(p); };
        r = out(f5, p2.point.clone(), 'Achsabstand ' + sh(p1) + ' ↔ ' + sh(p2), 'Achsabstand');
        if (f5.distanceTo(p1.point) > 1e-3) r.ext.push([p1.point.clone(), f5.clone()]);
        return r;
      }
    }
    // Punkt ↔ Fläche: Lot auf die Ebene
    if (t1 === 'E' || t2 === 'E') {
      var pf = t1 === 'E' ? p1 : p2, pp = t1 === 'E' ? p2 : p1, f6 = onPlane(pp.point, pf.plane);
      if (f6.distanceTo(pp.point) > 1e-3) return out(pp.point.clone(), f6, ptName(pp) + ' ↔ Fläche, senkrecht gemessen', 'senkrecht');
    }
    // Punkt ↔ Kante: Lot auf die verlängerte Kante
    if (t1 === 'L' || t2 === 'L') {
      var pk = t1 === 'L' ? p1 : p2, pq = t1 === 'L' ? p2 : p1, f7 = footOn(pq.point, pk.seg[0], dir(pk));
      if (f7.distanceTo(pq.point) > 1e-3) return withExt(out(pq.point.clone(), f7, ptName(pq) + ' ↔ Kante, senkrecht gemessen', 'senkrecht'), pk.seg, f7);
    }
    // Punkt ↔ Punkt
    r = out(p1.point.clone(), p2.point.clone(), p1.kind === 'kreis' && p2.kind === 'kreis' ? 'Mittelpunktabstand' : 'direkt ' + ptName(p1) + ' ↔ ' + ptName(p2));
    r.p2p = true; return r;
  }
  /** Ergebnis + Alternative (senkrecht ↔ direkt). useAlt tauscht beide — Umschalter in der Ergebnistafel. */
  function evalMeasure(T, p1, p2, useAlt) {
    var main = measureCore(T, p1, p2, 'auto'), alt = null;
    if (!main.p2p) alt = measureCore(T, p1, p2, 'direct');
    else if (p1.kind === 'mitte' || p2.kind === 'mitte') alt = measureCore(T, p1, p2, 'line');
    if (alt && alt.angle == null && main.angle == null && Math.abs(alt.d - main.d) < 5e-4) alt = null;   // gleicher Wert → kein Umschalter
    if (alt && alt.angle == null && alt.d < 1e-3) alt = null;                                            // 0,000 als Alternative hilft niemandem
    var r = useAlt && alt ? alt : main; r.alt = useAlt && alt ? main : alt; return r;
  }
  function altText(o) { return o.angle != null ? 'Winkel ' + fmt(o.angle, 2) + '°' : o.p2p ? 'direkt Punkt ↔ Punkt: ' + fmt(o.d, 3) + ' mm' : (o.tag === 'Achsabstand' ? 'Achsabstand: ' : 'senkrecht gemessen: ') + fmt(o.d, 3) + ' mm'; }

  // ── Tafeln ──
  P.renderInfo = function () {
    var m = this.model, root = this.root; if (!m) return; var T = this.THREE;
    var s = m.bbox.getSize(new T.Vector3()), raw = s.x * s.y * s.z / 1000, rho = Number(root.querySelector('.gw-sv-rho').value) || 2.7;
    root.querySelector('[data-k=bbox]').textContent = fmt(s.x) + ' × ' + fmt(s.y) + ' × ' + fmt(s.z) + ' mm';
    root.querySelector('[data-k=vol]').textContent = fmt(m.volumeCm3) + ' cm³';
    root.querySelector('[data-k=rem]').textContent = raw > 0 ? fmt(100 - m.volumeCm3 / raw * 100, 1) + ' % vom Rohquader' : '—';
    root.querySelector('[data-k=faces]').textContent = String(m.faces.length);
    var kg = m.volumeCm3 * rho; root.querySelector('[data-k=kg]').textContent = kg >= 1000 ? fmt(kg / 1000) + ' kg' : fmt(kg, 0) + ' g';
  };
  P.renderHoles = function () {
    var m = this.model, card = this.root.querySelector('.gw-sv-holes'), ul = card.querySelector('.gw-sv-holelist'), self = this; if (!m) return;
    var sum = m.holes.summary; card.hidden = sum.length === 0; card.querySelector('[data-k=hn]').textContent = '· ' + m.holes.list.length; ul.innerHTML = '';
    sum.forEach(function (h) {
      var ex = m.holes.list.filter(function (x) { return x.d === h.d; })[0];
      var li = document.createElement('li'), b = document.createElement('button'); b.type = 'button'; b.className = self.hiliteD === h.d ? 'is-on' : '';
      b.innerHTML = '<span style="color:inherit">' + h.n + ' × Ø ' + fmt(h.d) + '</span><span>' + (ex && ex.steps.length ? '→ Ø ' + ex.steps.map(function (x) { return fmt(x, 1); }).join('/') : ex && ex.senkung ? 'Senkung ' + fmt(ex.senkung, 0) + '°' : ex ? 't ' + fmt(ex.depth, 1) : '') + '</span>';
      b.addEventListener('click', function () { self.hiliteD = self.hiliteD === h.d ? null : h.d; self.selFaces = []; self.drawMarks(); self.renderHoles(); self.renderSel(); });
      li.appendChild(b); ul.appendChild(li);
    });
  };
  P.describe = function (gi) {
    var f = this.model.info[gi], areaL = 'Fläche ' + fmt(f.area / 100) + ' cm²';
    if (f.type === 'plane') {
      var lines = [areaL], m = this.model;
      if (m.baseFace >= 0 && m.baseFace !== gi) {
        var nb = m.info[m.baseFace].normal, c = Math.min(1, Math.abs(f.normal.dot(nb)));
        var ang = this.THREE.MathUtils.radToDeg(Math.acos(c));
        if (ang > 0.05 && ang < 179.95) lines.unshift('Neigung zur Grundfläche: ' + fmt(ang, 2) + '° (Öffnungswinkel ' + fmt(180 - ang, 2) + '°)');
        else if (ang <= 0.05) lines.unshift('parallel zur Grundfläche');
      }
      var tz = this.THREE.MathUtils.radToDeg(Math.acos(Math.min(1, Math.abs(f.normal.z))));
      if (tz > 0.05 && tz < 89.95) lines.push('Winkel zur XY-Ebene: ' + fmt(tz, 2) + '°');
      return { title: gi === m.baseFace ? 'Planfläche (Grundfläche)' : 'Planfläche', lines: lines };
    }
    var sw = f.sweepTotal != null ? f.sweepTotal : f.sweepDeg;
    if (f.type === 'cylinder' && f.hole && sw != null && sw <= 190) return { title: 'Innenradius R ' + fmt(f.radius) + ' mm', lines: ['Bogen ' + fmt(sw, 0) + '° · Länge ' + fmt(f.length) + ' mm', areaL] };   // Taschenecke, Nutende, Bogen
    if (f.type === 'cylinder') return f.hole ? { title: 'Bohrung Ø ' + fmt(f.radius * 2) + ' mm', lines: ['Tiefe/Länge ' + fmt(f.length) + ' mm', areaL] } : { title: 'Zylinder Ø ' + fmt(f.radius * 2) + ' mm', lines: ['Länge ' + fmt(f.length) + ' mm', areaL] };
    if (f.type === 'fillet') return { title: 'Rundung R ' + fmt(f.radius) + ' mm', lines: ['Kantenlänge ' + fmt(f.length) + ' mm · ' + fmt(f.sweepDeg, 0) + '°', areaL] };
    if (f.type === 'cone') return f.hole ? { title: 'Senkung ' + fmt(f.halfAngle * 2, 0) + '°', lines: ['Tiefe ' + fmt(f.length) + ' mm', areaL] } : { title: 'Fase ' + fmt(90 - f.halfAngle, 0) + '° × ' + fmt(f.length) + ' mm', lines: ['Kegel, halber Öffnungswinkel ' + fmt(f.halfAngle, 1) + '°', areaL] };
    if (f.type === 'rundung') return { title: 'Rundung (gekrümmt)', lines: [areaL] };
    return { title: 'Freiformfläche', lines: [areaL] };
  };
  P.relate = function (a, b) {
    var T = this.THREE, fa = this.model.info[a], fb = this.model.info[b], out = [];
    if (fa.type === 'plane' && fb.type === 'plane') { var cosang = Math.abs(fa.normal.dot(fb.normal)); if (isPar(cosang)) out.push('Abstand paralleler Planflächen: ' + fmt(Math.abs(fb.centroid.clone().sub(fa.centroid).dot(fa.normal)), 3) + ' mm'); else { var wa = T.MathUtils.radToDeg(Math.acos(Math.min(1, cosang))); out.push('Winkel zwischen den Flächen: ' + fmt(wa, 2) + '°'); out.push('Öffnungswinkel: ' + fmt(180 - wa, 2) + '°'); } }
    else if (fa.type === 'cylinder' && fb.type === 'cylinder') { if (isPar(fa.axis.dot(fb.axis))) { var w = fb.axisPoint.clone().sub(fa.axisPoint); out.push('Achsabstand: ' + fmt(w.addScaledVector(fa.axis, -w.dot(fa.axis)).length(), 3) + ' mm'); out.push('Ø ' + fmt(fa.radius * 2) + ' und Ø ' + fmt(fb.radius * 2) + ' mm'); } else out.push('Achsen nicht parallel (' + fmt(T.MathUtils.radToDeg(Math.acos(Math.min(1, Math.abs(fa.axis.dot(fb.axis))))), 1) + '°)'); }
    else if ((fa.type === 'plane' && fb.type === 'cylinder') || (fa.type === 'cylinder' && fb.type === 'plane')) { var pl = fa.type === 'plane' ? fa : fb, cy = fa.type === 'cylinder' ? fa : fb; if (Math.abs(pl.normal.dot(cy.axis)) < 0.002) out.push('Achse → Planfläche: ' + fmt(Math.abs(cy.axisPoint.clone().sub(pl.centroid).dot(pl.normal)), 3) + ' mm'); }
    out.push('Schwerpunktabstand: ' + fmt(fa.centroid.distanceTo(fb.centroid)) + ' mm');
    return out;
  };
  P.renderSel = function () {
    var card = this.root.querySelector('.gw-sv-sel'), body = card.querySelector('.gw-sv-sel-body'), self = this;
    this.updateHint(); this.renderResult();
    if (!this.model || !this.selFaces.length) { card.hidden = true; return; }
    card.hidden = false; body.innerHTML = '';
    this.selFaces.forEach(function (gi, k) { var d = self.describe(gi); var div = document.createElement('div'); div.innerHTML = '<b>' + (k + 1) + '. ' + d.title + '</b>' + d.lines.map(function (l) { return '<div class="m">' + l + '</div>'; }).join(''); body.appendChild(div); });
    if (this.selFaces.length === 2) { var rel = document.createElement('div'); rel.className = 'rel'; rel.innerHTML = this.relate(this.selFaces[0], this.selFaces[1]).map(function (l, i) { return '<div' + (i === 0 ? ' style="font-weight:600"' : '') + '>' + l + '</div>'; }).join(''); body.appendChild(rel); }
  };
  P.measureResult = function () { return this.model && this.measurePts.length === 2 ? evalMeasure(this.THREE, this.measurePts[0], this.measurePts[1], this.measAlt) : null; };
  function resultHtml(r) {
    return '<div class="gw-sv-rv">' + (r.angle != null ? fmt(r.angle, 2) + '°' : fmt(r.d, 3) + ' mm') + '</div>' +
      '<div class="gw-sv-rl">' + r.label + '</div>' +
      (r.angle == null ? '<div class="gw-sv-rd">' + r.delta + '</div>' : '') +
      (r.alt ? '<button type="button" class="gw-sv-alt" title="Messart umschalten">⇄ ' + altText(r.alt) + '</button>' : '');
  }
  P.renderMeas = function () {
    var card = this.root.querySelector('.gw-sv-meas'), body = card.querySelector('.gw-sv-meas-body'), self = this;
    this.updateHint(); this.renderResult();
    if (!this.measurePts.length) { card.hidden = true; return; }
    card.hidden = false; body.innerHTML = '';
    this.measurePts.forEach(function (p, k) { var row = document.createElement('div'); row.className = 'pt'; row.innerHTML = '<span class="m">P' + (k + 1) + ' <span style="color:#7f8f9f">' + nameOf(p) + '</span> (' + fmt(p.point.x) + ' · ' + fmt(p.point.y) + ' · ' + fmt(p.point.z) + ')</span>'; var x = document.createElement('button'); x.type = 'button'; x.textContent = '✕'; x.title = 'Punkt entfernen'; x.setAttribute('aria-label', 'Punkt ' + (k + 1) + ' entfernen'); x.addEventListener('click', function () { self.measurePts.splice(k, 1); self.measAlt = false; self.drawMarks(); self.renderMeas(); }); row.appendChild(x); body.appendChild(row); });
    var r = this.measureResult();
    if (r) { var d = document.createElement('div'); d.className = 'gw-sv-mres'; d.innerHTML = resultHtml(r); body.appendChild(d); }
    else { var hint = document.createElement('div'); hint.style.cssText = 'color:#7f8f9f;font-size:11.5px'; hint.textContent = 'zweiten Punkt anklicken'; body.appendChild(hint); }
  };
  /** Ergebnistafel IN der Bühne: bleibt in jedem Modus sichtbar — auch im Vollbild und auf dem Handy. */
  P.renderResult = function () {
    var el = this.root.querySelector('.gw-sv-result'); if (!el) return;
    var html = '', face = false, head = function (t, clr) { return '<div class="gw-sv-rh"><span>' + t + '</span><button type="button" class="gw-sv-rx" data-clear="' + clr + '" aria-label="löschen" title="löschen">✕</button></div>'; };
    if (this.model && this.tool === 'face' && this.selFaces.length) {
      face = true;
      if (this.selFaces.length === 2) {
        var rel = this.relate(this.selFaces[0], this.selFaces[1]);
        if (rel.length === 1) {   // nur Schwerpunktabstand → kein echtes Maß, nicht als Hauptwert zeigen
          html = head('Zwei Flächen', 'sel') + '<div class="gw-sv-rv sm">Kein direktes Maß für diese Kombination</div><div class="gw-sv-rl">Für Abstände oben „Messen“ wählen und Kante, Bohrungsrand oder Fläche anklicken.</div><div class="gw-sv-rd">' + rel[0] + '</div>';
        } else {
          html = head('Zwei Flächen', 'sel') + '<div class="gw-sv-rv sm">' + rel[0] + '</div>' + rel.slice(1).map(function (l) { return '<div class="gw-sv-rl">' + l + '</div>'; }).join('');
        }
      } else {
        // Planfläche: Neigungswinkel ist die Hauptaussage · Bohrung/Radius/Fase: der Titel (Ø, R, Winkel)
        var ds = this.describe(this.selFaces[0]), plane = this.model.info[this.selFaces[0]].type === 'plane';
        var top = plane ? ds.lines[0] : ds.title, rest = plane ? ds.lines.slice(1) : ds.lines;
        html = head(plane ? ds.title : 'Fläche', 'sel') + '<div class="gw-sv-rv sm">' + top + '</div>' + rest.map(function (l) { return '<div class="gw-sv-rl">' + l + '</div>'; }).join('');
      }
    } else {
      var r = this.measureResult();
      if (r) html = head(r.angle != null ? 'Winkel · Modellwert' : 'Abstand · Modellwert', 'meas') + resultHtml(r);
    }
    el.classList.toggle('is-face', face); el.hidden = !html; el.innerHTML = html;
    this.stage.classList.toggle('has-result', !!html);   // Handy: Hinweiszeile weicht der Ergebnistafel
  };

  // ── Übergabe ans Sofortangebot (gleiche Seite) ──
  P.handoff = function () {
    if (!this.file) return;
    var zielId = this.root.getAttribute('data-handoff') || 'sofortangebot';
    var ziel = document.getElementById(zielId) || document.querySelector('.gw-qp');
    if (this.fromTab) {   // Formular ist im anderen Tab: dorthin zurück, diesen Tab schließen
      // … aber nur, wenn es dort die Datei noch gibt (Tab zu, weggeklickt, Datei entfernt → sonst wäre alles weg)
      var ok = false;
      try {
        var op = window.opener, h = op && !op.closed && op.__gwHandoff;
        ok = !!(h && h.file && h.file.name === this.file.name && op.document.querySelector('.gw-qp-doc[data-art="cad"][data-da]'));
      } catch (e) { ok = false; }
      if (ok) {
        try { window.opener.focus(); } catch (e) {}
        var selfV = this; window.close();
        setTimeout(function () { if (!window.closed) selfV.status('Ihr Angebot ist im anderen Tab geöffnet — bitte dorthin wechseln.'); }, 400);
        return;
      }
      this.fromTab = false; this.updateCta();   // weiter mit der normalen Übergabe an das Formular dieser Seite
    }
    if (this.file.size > 30 * 1024 * 1024) {   // Preis-Server nimmt bis 30 MB
      this.status('Für den Sofortpreis sind Dateien bis 30 MB möglich. Größere Teile bitte als Anfrage über „Kontakt“ senden.');
      return;
    }
    if (this.fromPortal && ziel) {   // Datei ist schon im Formular → nur zurück, nicht erneut hochladen
      // Formular sichtbar lassen, wo es ist (Weg über den Vorschau-Knopf: Seite steht noch am Formular);
      // nur wenn es außer Sicht ist (Vollbild selbst geöffnet, Viewer unten auf der Seite), hinscrollen.
      var zeig = function () { var fx = ziel.querySelector('.gw-qp') || ziel, rr = fx.getBoundingClientRect(); if (rr.bottom < 120 || rr.top > window.innerHeight - 120) ziel.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
      // 250 ms: nach dem Ersatz-Vollbild läuft erst history.back() (Scroll-Wiederherstellung), dann prüfen wir
      if (this.root.classList.contains('is-full') && this.exitFull) { this.exitFull(function () { setTimeout(zeig, 250); }); return; }
      zeig();
      return;
    }
    var ev = new CustomEvent('gw:handoff', { detail: { file: this.file }, bubbles: true }); document.dispatchEvent(ev);
    // Im Vollbild wäre das Formular unsichtbar → erst Vollbild verlassen, dann hinscrollen
    var go = function () { if (ziel) ziel.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
    if (this.exitFull) this.exitFull(function () { setTimeout(go, 60); }); else go();
    if (!ev.defaultPrevented && !document.querySelector('.gw-qp')) window.location.href = '/pages/sofortangebot-cnc';
  };

  function scan() { document.querySelectorAll('[data-gw-step-viewer]').forEach(function (el) { if (!el.__gwsv) el.__gwsv = new Viewer(el); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  window.GWStepViewer = { scan: scan };
})();
